import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import { ChevronLeft, ChevronRight, ChevronDown, CheckSquare, Square, Printer, Plus, Edit2, X, Zap, Settings, ArrowLeft, Search, GitCommit, Route, User, Truck, Trash2, Focus, Clock, DollarSign, Navigation, RefreshCw, Check, AlertTriangle, Package, ListOrdered, Building, Pause, Sparkles, Activity, Radio, ArrowUpDown, Calendar } from 'lucide-react';
import StopCardModal from '../components/StopCardModal';
import ClientCardModal from '../components/ClientCardModal';
import JobCardModal from '../components/JobCardModal';
import JobOptimizationModal from '../components/JobOptimizationModal';
import JobOptimizationOptionsModal from '../components/JobOptimizationOptionsModal';
import CrossOptimizationOptionsModal from '../components/CrossOptimizationOptionsModal';
import CrossOptimizationModal from '../components/CrossOptimizationModal';
import DriverTimelinePanel from '../components/DriverTimelinePanel';
import CalendarFilterModal from '../components/CalendarFilterModal';
import { createTileLayer } from '../utils/mapTiles';
import { formatLocalTimeHHMM, parseToDate, formatDurationSec } from '../utils/dateUtils';

function timeStringToSec(timeStr = '08:00') {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(':').map(Number);
  return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60;
}

function checkTimeWindowStatus(estimatedArrivalStr = '08:00', twStartStr = '08:00:00', twEndStr = '18:00:00') {
  if (!estimatedArrivalStr) return { isValid: true };
  const arrSec = timeStringToSec(estimatedArrivalStr);
  const startSec = timeStringToSec(twStartStr || '07:00:00');
  const endSec = timeStringToSec(twEndStr || '19:00:00');
  return {
    isValid: arrSec >= startSec && arrSec <= endSec,
    arrSec,
    startSec,
    endSec
  };
}

function getStopWindowStartSec(stop, clientDefaultTimeWindows = []) {
  if (!stop) return null;
  if (Array.isArray(clientDefaultTimeWindows) && clientDefaultTimeWindows.length > 0 && stop.client_id) {
    const clientTWs = clientDefaultTimeWindows.filter(tw => Number(tw.client_id) === Number(stop.client_id));
    if (clientTWs.length > 0 && clientTWs[0].time_window_start) {
      return timeStringToSec(clientTWs[0].time_window_start);
    }
  }
  if (stop.time_windows) {
    let tws = stop.time_windows;
    if (typeof tws === 'string' && tws.trim().length > 0) {
      try { tws = JSON.parse(tws); } catch {}
    }
    if (Array.isArray(tws) && tws.length > 0 && tws[0].start) {
      return timeStringToSec(tws[0].start);
    }
  }
  if (stop.time_window_start) {
    return timeStringToSec(stop.time_window_start);
  }
  return null;
}

function calcDepartureTimeStr(arrivalStr = '08:00', serviceMins = 10, stop = null, clientDefaultTimeWindows = []) {
  if (!arrivalStr) return '08:10';
  const arrSec = timeStringToSec(arrivalStr);
  const windowStartSec = getStopWindowStartSec(stop, clientDefaultTimeWindows);
  const serviceStartSec = (windowStartSec !== null && arrSec < windowStartSec) ? windowStartSec : arrSec;
  const depSec = serviceStartSec + (parseInt(serviceMins || 10) * 60);
  const finalH = Math.floor((depSec / 3600) % 24);
  const finalM = Math.floor((depSec % 3600) / 60);
  return `${String(finalH).padStart(2, '0')}:${String(finalM).padStart(2, '0')}`;
}

function calcLegDriveTimeMins(currStop, nextStop, nextStopPauseMins = 0, clientDefaultTimeWindows = []) {
  if (nextStop?.leg_drive_mins !== undefined && nextStop?.leg_drive_mins !== null) {
    return Math.max(1, parseInt(nextStop.leg_drive_mins, 10));
  }
  if (!currStop?.estimated_arrival || !nextStop?.estimated_arrival) return 10;
  const currArrSec = timeStringToSec(currStop.estimated_arrival);
  const windowStartSec = getStopWindowStartSec(currStop, clientDefaultTimeWindows);
  const serviceStartSec = (windowStartSec !== null && currArrSec < windowStartSec) ? windowStartSec : currArrSec;
  const nextArrSec = timeStringToSec(nextStop.estimated_arrival);
  const serviceSec = (parseInt(currStop.service_time_mins || 10)) * 60;
  const driveSec = nextArrSec - (nextStopPauseMins * 60) - (serviceStartSec + serviceSec);
  return Math.max(1, Math.round(driveSec / 60));
}

function calcHubToStopDriveMins(startHub, firstStop, startJobTimeStr = '07:30', firstStopPauseMins = 0) {
  if (firstStop?.leg_drive_mins !== undefined && firstStop?.leg_drive_mins !== null) {
    return Math.max(1, parseInt(firstStop.leg_drive_mins, 10));
  }
  if (!firstStop?.estimated_arrival) return 15;
  const startSec = timeStringToSec(startJobTimeStr);
  const arrSec = timeStringToSec(firstStop.estimated_arrival);
  const driveSec = arrSec - (firstStopPauseMins * 60) - startSec;
  return Math.max(1, Math.round(driveSec / 60));
}

function calcStopToHubDriveMins(lastStop, endHub, returnTimeStr = '12:00', job = null, clientDefaultTimeWindows = []) {
  if (job?.return_leg_drive_mins !== undefined && job?.return_leg_drive_mins !== null) {
    return Math.max(1, parseInt(job.return_leg_drive_mins, 10));
  }
  if (!lastStop?.estimated_arrival) return 15;
  const arrSec = timeStringToSec(lastStop.estimated_arrival);
  const windowStartSec = getStopWindowStartSec(lastStop, clientDefaultTimeWindows);
  const serviceStartSec = (windowStartSec !== null && arrSec < windowStartSec) ? windowStartSec : arrSec;
  const serviceSec = (parseInt(lastStop.service_time_mins || 10)) * 60;
  const depSec = serviceStartSec + serviceSec;
  const retSec = timeStringToSec(returnTimeStr);
  return Math.max(1, Math.round((retSec - depSec) / 60));
}

function calcHaversineDistanceKm(lat1, lon1, lat2, lon2) {
  const p1 = parseFloat(lat1);
  const p2 = parseFloat(lon1);
  const p3 = parseFloat(lat2);
  const p4 = parseFloat(lon2);
  if (isNaN(p1) || isNaN(p2) || isNaN(p3) || isNaN(p4)) return 8.5;
  const R = 6371;
  const dLat = (p3 - p1) * Math.PI / 180;
  const dLon = (p4 - p2) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(p1 * Math.PI / 180) * Math.cos(p3 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c * 1.3;
  return Math.round(d * 10) / 10;
}

function calculateReturnTime(startTimeStr = '07:30', durationSec = 0) {
  if (!startTimeStr) startTimeStr = '07:30';
  const parts = String(startTimeStr).split(':').map(Number);
  const startH = !isNaN(parts[0]) ? parts[0] : 7;
  const startM = !isNaN(parts[1]) ? parts[1] : 30;
  const totalSec = startH * 3600 + startM * 60 + Math.round(durationSec);
  const finalH = Math.floor((totalSec / 3600) % 24);
  const finalM = Math.floor((totalSec % 3600) / 60);
  return `${String(finalH).padStart(2, '0')}:${String(finalM).padStart(2, '0')}`;
}

/**
 * Calculates peak cumulative cargo load (weight and volume) along the route.
 * Initial load = sum of all deliveries (loaded at hub).
 * Each delivery stop unloads cargo (-), each pickup stop adds cargo (+).
 */
function calculateTruckPeakPayload(truck, jobStopsList = []) {
  let deliveriesWeight = 0;
  let deliveriesVolume = 0;

  jobStopsList.forEach(s => {
    const w = parseFloat(s.weight_kg || 0);
    const v = parseFloat(s.volume_m3 || 0);
    if (s.type !== 'pickup') {
      deliveriesWeight += w;
      deliveriesVolume += v;
    }
  });

  let currentWeight = deliveriesWeight;
  let currentVolume = deliveriesVolume;

  let peakWeight = currentWeight;
  let peakVolume = currentVolume;

  jobStopsList.forEach(s => {
    const w = parseFloat(s.weight_kg || 0);
    const v = parseFloat(s.volume_m3 || 0);

    if (s.type === 'pickup') {
      currentWeight += w;
      currentVolume += v;
    } else {
      currentWeight -= w;
      currentVolume -= v;
    }

    if (currentWeight > peakWeight) peakWeight = currentWeight;
    if (currentVolume > peakVolume) peakVolume = currentVolume;
  });

  if (!truck) {
    return {
      peakWeight: Math.round(peakWeight),
      peakVolume: Math.round(peakVolume * 10) / 10,
      maxWeight: '--',
      maxVolume: '--',
      isOverweight: false,
      isOvervolume: false
    };
  }

  const maxWeight = parseFloat(truck.max_payload_weight_kg || 1200);
  const maxVolume = parseFloat(truck.max_payload_volume_m3 || 14.0);

  const isOverweight = peakWeight > maxWeight;
  const isOvervolume = peakVolume > maxVolume;

  return {
    peakWeight: Math.round(peakWeight),
    peakVolume: Math.round(peakVolume * 10) / 10,
    maxWeight: Math.round(maxWeight),
    maxVolume: Math.round(maxVolume * 10) / 10,
    isOverweight,
    isOvervolume
  };
}

function formatWeightPair(peakWeightKg, maxWeightKg) {
  const peak = parseFloat(peakWeightKg || 0);
  const isMaxUnassigned = maxWeightKg === '--';
  const max = isMaxUnassigned ? null : parseFloat(maxWeightKg);

  const useTons = peak >= 1000 || (max !== null && max >= 1000);

  if (useTons) {
    const peakTons = (peak / 1000).toFixed(1);
    if (isMaxUnassigned) {
      return `${peakTons}/--t`;
    }
    const maxTons = (max / 1000).toFixed(1);
    return `${peakTons}/${maxTons}t`;
  }

  const peakKg = Math.round(peak);
  if (isMaxUnassigned) {
    return `${peakKg}/--kg`;
  }
  return `${peakKg}/${Math.round(max)}kg`;
}

function renderStopRealTimeWindows(stop, client, arrivalTimeFormatted = null, clientDefaultTimeWindows = []) {
  let stopTimeWindows = [];

  const clientId = client?.id || stop?.client_id;
  const clientTWs = (clientDefaultTimeWindows || []).filter(tw => tw.client_id === clientId);

  if (clientTWs.length > 0) {
    stopTimeWindows = clientTWs.map(tw => ({
      start: tw.time_window_start ? tw.time_window_start.slice(0, 5) : '08:00',
      end: tw.time_window_end ? tw.time_window_end.slice(0, 5) : '18:00'
    }));
  } else if (Array.isArray(stop?.time_windows) && stop.time_windows.length > 0) {
    stopTimeWindows = stop.time_windows.map(tw => ({
      start: tw.start ? tw.start.slice(0, 5) : '08:00',
      end: tw.end ? tw.end.slice(0, 5) : '18:00'
    }));
  } else if (stop?.time_window_start) {
    stopTimeWindows = [{
      start: stop.time_window_start.slice(0, 5),
      end: (stop.time_window_end || '18:00').slice(0, 5)
    }];
  } else {
    stopTimeWindows = [{ start: '08:00', end: '18:00' }];
  }

  const arrSec = arrivalTimeFormatted ? timeStringToSec(arrivalTimeFormatted) : null;

  let isAnyHit = false;
  if (arrSec !== null) {
    isAnyHit = stopTimeWindows.some(tw => {
      const sSec = timeStringToSec(tw.start);
      const eSec = timeStringToSec(tw.end);
      return arrSec >= sSec && arrSec <= eSec;
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center', justifyContent: 'center' }}>
      {stopTimeWindows.map((tw, i) => {
        let isHit = false;
        if (arrSec !== null) {
          const sSec = timeStringToSec(tw.start);
          const eSec = timeStringToSec(tw.end);
          isHit = arrSec >= sSec && arrSec <= eSec;
        }

        let bg = '#1e293b';
        let color = '#94a3b8';
        let border = '1px solid #334155';

        if (arrSec !== null) {
          if (isHit) {
            bg = 'rgba(34, 197, 94, 0.2)';
            color = '#22c55e';
            border = '1px solid #166534';
          } else if (isAnyHit) {
            bg = '#0f172a';
            color = '#64748b';
            border = '1px solid #334155';
          } else {
            bg = 'rgba(239, 68, 68, 0.25)';
            color = '#ef4444';
            border = '1px solid #991b1b';
          }
        }

        return (
          <span
            key={i}
            style={{
              background: bg,
              color: color,
              border: border,
              padding: '1px 5px',
              borderRadius: '3px',
              fontSize: '0.66rem',
              fontWeight: arrSec !== null ? '700' : '500',
              whiteSpace: 'nowrap',
              lineHeight: '1.2'
            }}
          >
            {tw.start} - {tw.end}
          </span>
        );
      })}
    </div>
  );
}

function renderRealtimeClockIcon(stop, client, validationTimeFormatted = null, clientDefaultTimeWindows = []) {
  let stopTimeWindows = [];
  const clientId = client?.id || stop?.client_id;
  const clientTWs = (clientDefaultTimeWindows || []).filter(tw => tw.client_id === clientId);

  if (clientTWs.length > 0) {
    stopTimeWindows = clientTWs.map(tw => ({
      start: tw.time_window_start ? tw.time_window_start.slice(0, 5) : '08:00',
      end: tw.time_window_end ? tw.time_window_end.slice(0, 5) : '18:00'
    }));
  } else if (Array.isArray(stop?.time_windows) && stop.time_windows.length > 0) {
    stopTimeWindows = stop.time_windows.map(tw => ({
      start: tw.start ? tw.start.slice(0, 5) : '08:00',
      end: tw.end ? tw.end.slice(0, 5) : '18:00'
    }));
  } else if (stop?.time_window_start) {
    stopTimeWindows = [{
      start: stop.time_window_start.slice(0, 5),
      end: (stop.time_window_end || '18:00').slice(0, 5)
    }];
  }

  // If no time window defined
  if (stopTimeWindows.length === 0) {
    return (
      <div title="Aucune plage horaire définie" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Clock size={16} color="#64748b" />
      </div>
    );
  }

  const twStrList = stopTimeWindows.map(tw => `${tw.start} - ${tw.end}`).join(', ');
  const isBucket = stop.job_id === null || stop.job_id === undefined;

  if (isBucket || !validationTimeFormatted || validationTimeFormatted === '--') {
    return (
      <div title={`Plage horaire : ${twStrList}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Clock size={16} color="#64748b" />
      </div>
    );
  }

  const valSec = timeStringToSec(validationTimeFormatted);
  const isHit = stopTimeWindows.some(tw => {
    const sSec = timeStringToSec(tw.start);
    const eSec = timeStringToSec(tw.end);
    return valSec >= sSec && valSec <= eSec;
  });

  if (isHit) {
    return (
      <div title={`Validé dans la plage horaire (${twStrList})`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Clock size={16} color="#22c55e" />
      </div>
    );
  } else {
    return (
      <div title={`Validé hors plage horaire (${twStrList})`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Clock size={16} color="#ef4444" />
      </div>
    );
  }
}

// Debounced & immediate local editing for job column view title
function JobTitleInput({ jobId, initialValue, placeholder, onSave }) {
  const [val, setVal] = useState(initialValue || '');
  const latestSavedVal = useRef(initialValue || '');
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    setVal(initialValue || '');
    latestSavedVal.current = initialValue || '';
  }, [initialValue]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleCommit = (newVal) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    const currentVal = newVal !== undefined ? newVal : val;
    const trimmed = (currentVal || '').trim();
    if (trimmed !== (latestSavedVal.current || '').trim()) {
      latestSavedVal.current = trimmed;
      if (onSave) {
        onSave(jobId, { display_name: trimmed });
      }
    }
  };

  const handleChange = (e) => {
    const nextVal = e.target.value;
    setVal(nextVal);

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      handleCommit(nextVal);
    }, 1000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      setVal(latestSavedVal.current || '');
      e.target.blur();
    }
  };

  return (
    <input
      type="text"
      placeholder={placeholder}
      style={{
        background: 'transparent',
        border: 'none',
        borderBottom: '1px dashed #38bdf8',
        color: val ? '#ffffff' : '#94a3b8',
        fontWeight: '800',
        fontSize: '0.95rem',
        width: '100%',
        outline: 'none',
        padding: '2px 0'
      }}
      value={val}
      onChange={handleChange}
      onBlur={() => handleCommit()}
      onKeyDown={handleKeyDown}
    />
  );
}

export default function ExpeditionPage({
  hubs = [],
  jobs = [],
  stops = [],
  clients = [],
  clientDefaultTimeWindows = [],
  clientContacts = [],
  onAddContact,
  onDeleteContact,
  onAddClientTimeWindow,
  onUpdateClientTimeWindow,
  onDeleteClientTimeWindow,
  onUpdateClient,
  drivers = [],
  driverShifts = [],
  trucks = [],
  rounds = [],
  unassignedStops = [],
  stopDependencies = [],
  jobPauses = [],
  realtimeEvents = [],
  onCalculateJob,
  onFetchOptimizationPreview,
  onApplyOptimization,
  onOptimizeJob,
  onCrossOptimize,
  onAssignStop,
  onUpdateStop,
  onDeleteStop,
  onUpdateJob,
  onDeleteJob,
  onReorderStops,
  onCreateJob,
  onAddPause,
  onDeletePause,
  onReloadData,
  onDeleteAllPlannedJobs,
  dateFilter: propDateFilter,
  onDateFilterChange,
  availableDates = []
}) {
  const [openJobIds, setOpenJobIds] = useState([]);
  const [showUnassignedDrawer, setShowUnassignedDrawer] = useState(false);
  const [unassignedSelectedRoundIds, setUnassignedSelectedRoundIds] = useState([]);
  const [unassignedSortBy, setUnassignedSortBy] = useState('time_window');
  const [unassignedSortAsc, setUnassignedSortAsc] = useState(true);
  const [showRoundFilterDropdown, setShowRoundFilterDropdown] = useState(false);
  const [showChronologicalDrawer, setShowChronologicalDrawer] = useState(false);
  const [showAllRealtimeStops, setShowAllRealtimeStops] = useState(false);
  const [showDriverTimelinePanel, setShowDriverTimelinePanel] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [confirmDeleteJobId, setConfirmDeleteJobId] = useState(null);

  // Single Selected Stop State
  const [selectedStopId, setSelectedStopId] = useState(null);

  // Modal Popups
  const [selectedStopModal, setSelectedStopModal] = useState(null);
  const [selectedClientModal, setSelectedClientModal] = useState(null);
  const [selectedJobModal, setSelectedJobModal] = useState(null);

  // Optimization Modal & Options State
  const [selectedJobForOptions, setSelectedJobForOptions] = useState(null);
  const [optimizationPreviewData, setOptimizationPreviewData] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [optimizingJobId, setOptimizingJobId] = useState(null);

  const [showSearchInput, setShowSearchInput] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Expedition Date Selection State (Supports single date and date range)
  const formatDateISO = (date) => {
    const d = date instanceof Date ? date : new Date(date || Date.now());
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDateFormatted = (date) => {
    const d = date instanceof Date ? date : new Date(date || Date.now());
    const frenchMonths = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
    return `${d.getDate()} ${frenchMonths[d.getMonth()]} ${d.getFullYear()}`;
  };

  const defaultDateStr = formatDateISO(new Date());
  const dateFilter = propDateFilter || { startDate: defaultDateStr, endDate: defaultDateStr, isAll: false };
  const [showDatePicker, setShowDatePicker] = useState(false);

  const selectedDateISO = dateFilter.startDate || defaultDateStr;

  useEffect(() => {
    window.__currentExpeditionDate = selectedDateISO;
  }, [selectedDateISO]);

  const handleApplyDateFilter = (newFilter) => {
    if (onDateFilterChange) {
      onDateFilterChange(newFilter);
    } else if (onReloadData) {
      onReloadData(newFilter);
    }
  };

  const getHeaderDateBadgeText = () => {
    if (dateFilter.isAll) return 'Toutes les dates';
    if (!dateFilter.startDate) return formatDateFormatted(new Date());
    if (dateFilter.startDate === dateFilter.endDate) {
      const [y, m, d] = dateFilter.startDate.split('-').map(Number);
      return formatDateFormatted(new Date(y, m - 1, d));
    }
    const [sy, sm, sd] = dateFilter.startDate.split('-').map(Number);
    const [ey, em, ed] = dateFilter.endDate.split('-').map(Number);
    const startObj = new Date(sy, sm - 1, sd);
    const endObj = new Date(ey, em - 1, ed);
    const frenchMonths = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
    if (startObj.getFullYear() === endObj.getFullYear()) {
      if (startObj.getMonth() === endObj.getMonth()) {
        return `${startObj.getDate()} - ${endObj.getDate()} ${frenchMonths[endObj.getMonth()]} ${endObj.getFullYear()}`;
      }
      return `${startObj.getDate()} ${frenchMonths[startObj.getMonth()]} - ${endObj.getDate()} ${frenchMonths[endObj.getMonth()]} ${endObj.getFullYear()}`;
    }
    return `${formatDateFormatted(startObj)} - ${formatDateFormatted(endObj)}`;
  };

  // Cross-Optimization Panel & Collapsible State
  const [showCrossOptiPanel, setShowCrossOptiPanel] = useState(false);
  const [crossOptiObjective, setCrossOptiObjective] = useState('cost'); // 'cost' | 'distance' | 'duration'
  const [crossOptiFlexibleStart, setCrossOptiFlexibleStart] = useState(true);
  const [crossOptiTimeWindows, setCrossOptiTimeWindows] = useState(true);
  const [crossOptiEnforceDependencies, setCrossOptiEnforceDependencies] = useState(true);
  const [crossOptiEnforceWeight, setCrossOptiEnforceWeight] = useState(true);
  const [crossOptiEnforceVolume, setCrossOptiEnforceVolume] = useState(true);
  const [crossOptiMaxHours, setCrossOptiMaxHours] = useState(8);
  const [crossOptiEnforceForcedPauses, setCrossOptiEnforceForcedPauses] = useState(false);
  const [crossOptiForcedPauses, setCrossOptiForcedPauses] = useState([
    { id: 1, duration_mins: 30, start_time: '12:00', end_time: '14:00' }
  ]);
  const [truckTimeWindows, setTruckTimeWindows] = useState({}); // { [truckId]: { startTime: '07:30', endTime: '17:00', driverId: null, shifts: [{ startTime: '07:30', endTime: '17:00' }] } }

  const handleUpdateTruckTW = (truckId, field, val) => {
    setTruckTimeWindows(prev => {
      const existing = prev[truckId] || { startTime: '07:30', endTime: '17:00', driverId: null, shifts: [{ startTime: '07:30', endTime: '17:00' }] };
      return {
        ...prev,
        [truckId]: { ...existing, [field]: val }
      };
    });
  };

  const handleAddShift = (truckId) => {
    setTruckTimeWindows(prev => {
      const existing = prev[truckId] || { startTime: '07:30', endTime: '17:00', driverId: null, shifts: [{ startTime: '07:30', endTime: '17:00' }] };
      const newShifts = [...(existing.shifts || [{ startTime: '07:30', endTime: '17:00' }]), { startTime: '07:30', endTime: '17:00' }];
      return { ...prev, [truckId]: { ...existing, shifts: newShifts } };
    });
  };

  const handleRemoveShift = (truckId, shiftIdx) => {
    setTruckTimeWindows(prev => {
      const existing = prev[truckId] || { shifts: [{ startTime: '07:30', endTime: '17:00' }] };
      const newShifts = (existing.shifts || []).filter((_, i) => i !== shiftIdx);
      if (newShifts.length === 0) newShifts.push({ startTime: '07:30', endTime: '17:00' });
      return { ...prev, [truckId]: { ...existing, shifts: newShifts } };
    });
  };

  const handleUpdateShift = (truckId, shiftIdx, field, val) => {
    setTruckTimeWindows(prev => {
      const existing = prev[truckId] || { shifts: [{ startTime: '07:30', endTime: '17:00' }] };
      const newShifts = [...(existing.shifts || [{ startTime: '07:30', endTime: '17:00' }])];
      newShifts[shiftIdx] = { ...newShifts[shiftIdx], [field]: val };
      return { ...prev, [truckId]: { ...existing, shifts: newShifts, startTime: newShifts[0].startTime, endTime: newShifts[0].endTime } };
    });
  };

  const handleSmartAssignDrivers = () => {
    if (selectedTruckIdsForOpti.length === 0) return;

    const licenseRanks = { 'B': 1, 'BE': 2, 'C1': 3, 'C': 4, 'CE': 5 };

    const isLicenseMatch = (driverPermit = 'B', truckRequiredPermit = 'B') => {
      const dRank = licenseRanks[String(driverPermit).toUpperCase()] || 1;
      const tRank = licenseRanks[String(truckRequiredPermit).toUpperCase()] || 1;
      return dRank >= tRank;
    };

    const targetDateStr = selectedDateISO;

    const shiftsForToday = (driverShifts || []).filter(s => {
      const sDate = String(s.start_datetime).slice(0, 10);
      return sDate === targetDateStr;
    });

    const hasSystemShifts = (driverShifts || []).length > 0;

    const usedDriverIds = new Set();
    const newTruckTW = { ...truckTimeWindows };

    selectedTruckIdsForOpti.forEach(truckId => {
      const truck = trucks.find(t => t.id === truckId);
      if (!truck) return;

      const eligibleDriver = drivers.find(d => {
        if (usedDriverIds.has(d.id)) return false;

        // 1. License match
        if (!isLicenseMatch(d.driving_license, truck.required_license)) {
          return false;
        }

        // 2. Availability check
        if (hasSystemShifts) {
          const dShifts = shiftsForToday.filter(s => s.driver_id === d.id);
          if (dShifts.length === 0) {
            return false;
          }
        }

        return true;
      });

      if (eligibleDriver) {
        usedDriverIds.add(eligibleDriver.id);

        const dShifts = shiftsForToday.filter(s => s.driver_id === eligibleDriver.id);
        let formattedShifts = [{ startTime: '07:30', endTime: '17:00' }];

        if (dShifts.length > 0) {
          formattedShifts = dShifts.map(s => {
            const sTime = String(s.start_datetime).includes('T') ? String(s.start_datetime).split('T')[1].slice(0, 5) : String(s.start_datetime).split(' ')[1]?.slice(0, 5) || '07:30';
            const eTime = String(s.end_datetime).includes('T') ? String(s.end_datetime).split('T')[1].slice(0, 5) : String(s.end_datetime).split(' ')[1]?.slice(0, 5) || '17:00';
            return { startTime: sTime, endTime: eTime };
          });
        }

        newTruckTW[truckId] = {
          ...(newTruckTW[truckId] || {}),
          driverId: eligibleDriver.id,
          startTime: formattedShifts[0].startTime,
          endTime: formattedShifts[0].endTime,
          shifts: formattedShifts
        };
      } else {
        // Empty dropdown if none can be found
        newTruckTW[truckId] = {
          ...(newTruckTW[truckId] || {}),
          driverId: '',
          startTime: newTruckTW[truckId]?.startTime || '07:30',
          endTime: newTruckTW[truckId]?.endTime || '17:00',
          shifts: newTruckTW[truckId]?.shifts || [{ startTime: '07:30', endTime: '17:00' }]
        };
      }
    });

    setTruckTimeWindows(newTruckTW);
  };

  const [selectedUnassignedStopIds, setSelectedUnassignedStopIds] = useState([]);
  const [selectedJobIdsForOpti, setSelectedJobIdsForOpti] = useState([]);
  const [selectedTruckIdsForOpti, setSelectedTruckIdsForOpti] = useState([]);
  const [isCrossOptiLoading, setIsCrossOptiLoading] = useState(false);
  const [crossOptiPreviewData, setCrossOptiPreviewData] = useState(null);
  const [showCrossOptiOptionsModal, setShowCrossOptiOptionsModal] = useState(false);
  const [showCrossOptiModal, setShowCrossOptiModal] = useState(false);
  const [markerColorMode, setMarkerColorMode] = useState('status'); // 'status' | 'truck'

  // Collapsible section states (Livraisons closed by default, others open)
  const [isCrossOptiUnassignedCollapsed, setIsCrossOptiUnassignedCollapsed] = useState(true);
  const [isCrossOptiJobsCollapsed, setIsCrossOptiJobsCollapsed] = useState(false);
  const [isCrossOptiTrucksCollapsed, setIsCrossOptiTrucksCollapsed] = useState(false);
  const [isCrossOptiOptionsCollapsed, setIsCrossOptiOptionsCollapsed] = useState(false);

  // Batch Rounds Creation Drawer State
  const [showRoundsBatchDrawer, setShowRoundsBatchDrawer] = useState(false);
  const [isBatchCreating, setIsBatchCreating] = useState(false);

  // Rich Drag & Drop State
  const [dragItem, setDragItem] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);

  // Live Driver Geohistory & Breadcrumbs State
  const [geohistoryByJobId, setGeohistoryByJobId] = useState({});

  const mapContainerRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersGroupRef = useRef(null);
  const tileLayerRef = useRef(null);

  useEffect(() => {
    const handleMapStyleChanged = () => {
      if (leafletMapRef.current && tileLayerRef.current) {
        try {
          leafletMapRef.current.removeLayer(tileLayerRef.current);
          const newLayer = createTileLayer().addTo(leafletMapRef.current);
          newLayer.bringToBack();
          tileLayerRef.current = newLayer;
        } catch (e) {
          console.warn('Error hot-swapping map tile layer:', e);
        }
      }
    };
    window.addEventListener('loops_map_style_changed', handleMapStyleChanged);
    return () => window.removeEventListener('loops_map_style_changed', handleMapStyleChanged);
  }, []);

  useEffect(() => {
    if (showCrossOptiPanel) {
      setSelectedUnassignedStopIds([]);
      setSelectedJobIdsForOpti([]);
      setSelectedTruckIdsForOpti([]);
      setIsCrossOptiUnassignedCollapsed(true);
      setIsCrossOptiJobsCollapsed(false);
      setIsCrossOptiTrucksCollapsed(false);
      setIsCrossOptiOptionsCollapsed(false);
    }
  }, [showCrossOptiPanel]);

  const isTodayDate = selectedDateISO === formatDateISO(new Date());

  const extractDateStr = (val) => {
    if (!val) return null;
    const str = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      const d = new Date(str);
      if (!isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
      return str.slice(0, 10);
    }
    return null;
  };

  const isDateInRange = (dateStr) => {
    if (!dateStr) return false;
    if (dateFilter.isAll) return true;
    const start = dateFilter.startDate;
    const end = dateFilter.endDate || dateFilter.startDate;
    if (!start) return true;
    return dateStr >= start && dateStr <= end;
  };

  const isStopOnSelectedDate = (stop) => {
    if (dateFilter.isAll) return true;

    // 1. Primary check: delivery_date
    if (stop.delivery_date) {
      const stopDateStr = extractDateStr(stop.delivery_date);
      if (isDateInRange(stopDateStr)) return true;
    }

    // 2. Secondary timestamp checks
    if (stop.actual_arrival && isDateInRange(extractDateStr(stop.actual_arrival))) return true;
    if (stop.estimated_arrival && isDateInRange(extractDateStr(stop.estimated_arrival))) return true;
    if (stop.real_departure && isDateInRange(extractDateStr(stop.real_departure))) return true;

    return false;
  };

  const isJobOnSelectedDate = (job) => {
    if (dateFilter.isAll) return true;

    // 1. Primary check: check if any stop of this job belongs to selected date
    const jobStops = stops.filter(s => s.job_id === job.id);
    if (jobStops.length > 0) {
      return jobStops.some(s => isStopOnSelectedDate(s));
    }

    // 2. Secondary check on real_start or real_end
    if (job.real_start && isDateInRange(extractDateStr(job.real_start))) return true;
    if (job.real_end && isDateInRange(extractDateStr(job.real_end))) return true;

    // 3. Fallback check for empty jobs (0 stops): check created_at date
    if (job.created_at && isDateInRange(extractDateStr(job.created_at))) return true;

    return false;
  };

  const getJobDisplayName = (job) => {
    if (job?.display_name && job.display_name.trim().length > 0) {
      return job.display_name.trim();
    }
    const round = (rounds || []).find(r => r.id === job?.round_id);
    if (round?.name && round.name.trim().length > 0) {
      return round.name.trim();
    }
    return 'Nouvelle mission';
  };

  const filteredJobs = jobs
    .filter(isJobOnSelectedDate)
    .sort((a, b) => {
      const nameA = getJobDisplayName(a);
      const nameB = getJobDisplayName(b);
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
  const assignedStopIds = new Set(stops.filter(s => s.job_id !== null).map(s => s.id));
  const activeUnassignedStops = (unassignedStops || [])
    .filter(us => !assignedStopIds.has(us.id))
    .filter(isStopOnSelectedDate);

  const roundsWithAvailableStops = (rounds || []).map(r => {
    const roundStops = activeUnassignedStops.filter(us => {
      const client = clients.find(c => c.id === us.client_id);
      return (us.default_round_id || client?.default_round_id) === r.id;
    });
    return { round: r, count: roundStops.length };
  }).filter(item => item.count > 0);

  const totalAvailableStopsInRounds = roundsWithAvailableStops.reduce((sum, item) => sum + item.count, 0);

  // Realtime Live Geohistory Tracking: Listen for DRIVER_LOCATION_UPDATE events
  useEffect(() => {
    const handleDriverLocation = (e) => {
      const point = e.detail;
      if (!point || !point.job_id) return;
      setGeohistoryByJobId(prev => {
        const existing = prev[point.job_id] || [];
        if (existing.some(p => (point.id && p.id === point.id) || p.recorded_at === point.recorded_at)) {
          return prev;
        }
        return {
          ...prev,
          [point.job_id]: [...existing, point]
        };
      });
    };

    window.addEventListener('loops:driver-location', handleDriverLocation);
    return () => window.removeEventListener('loops:driver-location', handleDriverLocation);
  }, []);

  // Fetch geohistories for ongoing and completed jobs on the active expedition view
  useEffect(() => {
    const relevantJobs = filteredJobs.filter(j => 
      j.status === 'in_progress' || j.status === 'completed' || j.status === 'done'
    );
    if (relevantJobs.length === 0) return;

    const jobIds = relevantJobs.map(j => j.id).join(',');
    let isCancelled = false;

    const fetchGeohistories = async () => {
      try {
        const res = await fetch(`/api/driver-geohistory?job_ids=${jobIds}`);
        if (res.ok && !isCancelled) {
          const data = await res.json();
          if (Array.isArray(data)) {
            const map = {};
            data.forEach(pt => {
              if (pt.job_id) {
                if (!map[pt.job_id]) map[pt.job_id] = [];
                map[pt.job_id].push(pt);
              }
            });
            setGeohistoryByJobId(prev => ({
              ...prev,
              ...map
            }));
          }
        }
      } catch (err) {
        console.error('Error fetching geohistories for expedition jobs:', err);
      }
    };

    fetchGeohistories();

    // Poll every 10 seconds for ongoing jobs to ensure live freshness
    const hasOngoing = relevantJobs.some(j => j.status === 'in_progress');
    let interval = null;
    if (hasOngoing) {
      interval = setInterval(fetchGeohistories, 10000);
    }

    return () => {
      isCancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [filteredJobs.map(j => `${j.id}:${j.status}`).join(',')]);

  const handleCreateAllMissionsWithStops = async () => {
    if (roundsWithAvailableStops.length === 0 || isBatchCreating) return;
    setIsBatchCreating(true);
    try {
      const roundIds = roundsWithAvailableStops.map(item => item.round.id);
      const res = await fetch('/api/rounds/create-batch-jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          round_ids: roundIds,
          include_bucket_stops: true,
          date: selectedDateISO,
          start_date: dateFilter?.startDate,
          end_date: dateFilter?.endDate,
          all: dateFilter?.isAll
        })
      });

      if (res.ok) {
        setOpenJobIds([]);
        if (onReloadData) await onReloadData();
      } else {
        for (const item of roundsWithAvailableStops) {
          await fetch(`/api/rounds/${item.round.id}/create-job`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              include_bucket_stops: true,
              date: selectedDateISO,
              start_date: dateFilter?.startDate,
              end_date: dateFilter?.endDate,
              all: dateFilter?.isAll
            })
          });
        }
        setOpenJobIds([]);
        if (onReloadData) await onReloadData();
      }
    } catch (err) {
      console.error('Error creating batch missions from rounds:', err);
    } finally {
      setIsBatchCreating(false);
    }
  };

  const getStopStartTimeSec = (stop) => {
    const client = clients.find(c => c.id === stop.client_id);
    const clientTWs = (clientDefaultTimeWindows || []).filter(tw => tw.client_id === (client?.id || stop?.client_id));
    if (clientTWs.length > 0 && clientTWs[0].time_window_start) {
      return timeStringToSec(clientTWs[0].time_window_start.slice(0, 5));
    }
    if (Array.isArray(stop.time_windows) && stop.time_windows.length > 0 && stop.time_windows[0].start) {
      return timeStringToSec(stop.time_windows[0].start.slice(0, 5));
    }
    if (stop.time_window_start) {
      return timeStringToSec(stop.time_window_start.slice(0, 5));
    }
    return 8 * 3600;
  };

  // Filter unassigned stops by selected rounds
  let displayedUnassignedStops = activeUnassignedStops;
  if (unassignedSelectedRoundIds.length > 0) {
    displayedUnassignedStops = displayedUnassignedStops.filter(us => {
      const client = clients.find(c => c.id === us.client_id);
      const roundId = us.default_round_id || client?.default_round_id;
      if (roundId) {
        return unassignedSelectedRoundIds.includes(roundId);
      }
      return unassignedSelectedRoundIds.includes('none');
    });
  }

  // Sort displayed unassigned stops
  displayedUnassignedStops = [...displayedUnassignedStops].sort((a, b) => {
    let cmp = 0;
    if (unassignedSortBy === 'time_window') {
      cmp = getStopStartTimeSec(a) - getStopStartTimeSec(b);
    } else if (unassignedSortBy === 'client') {
      cmp = (a.client_name || '').localeCompare(b.client_name || '', 'fr', { sensitivity: 'base' });
    } else if (unassignedSortBy === 'city') {
      cmp = (a.city || '').localeCompare(b.city || '', 'fr', { sensitivity: 'base' }) || (a.address || '').localeCompare(b.address || '', 'fr', { sensitivity: 'base' });
    } else if (unassignedSortBy === 'weight') {
      cmp = (parseFloat(b.weight_kg) || 0) - (parseFloat(a.weight_kg) || 0);
    } else if (unassignedSortBy === 'volume') {
      cmp = (parseFloat(b.volume_m3) || 0) - (parseFloat(a.volume_m3) || 0);
    } else if (unassignedSortBy === 'service_time') {
      const sA = a.service_time_mins || clients.find(c => c.id === a.client_id)?.default_service_time_mins || 10;
      const sB = b.service_time_mins || clients.find(c => c.id === b.client_id)?.default_service_time_mins || 10;
      cmp = sB - sA;
    } else if (unassignedSortBy === 'round') {
      const rA = rounds.find(r => r.id === (a.default_round_id || clients.find(c => c.id === a.client_id)?.default_round_id))?.name || '';
      const rB = rounds.find(r => r.id === (b.default_round_id || clients.find(c => c.id === b.client_id)?.default_round_id))?.name || '';
      cmp = rA.localeCompare(rB, 'fr', { sensitivity: 'base' });
    }
    return unassignedSortAsc ? cmp : -cmp;
  });

  const selectedJobStopsCount = stops.filter(s => selectedJobIdsForOpti.includes(s.job_id)).length;
  const totalDeliveriesToOpti = selectedUnassignedStopIds.length + selectedJobStopsCount;
  const allTrucksHaveDrivers = selectedTruckIdsForOpti.every(id => truckTimeWindows[id]?.driverId);

  const handleRunCrossOptimizationPreview = async () => {
    if (totalDeliveriesToOpti === 0 || selectedTruckIdsForOpti.length === 0) return;

    // Validate pickup & delivery dependencies if enforced
    if (crossOptiEnforceDependencies) {
      const selectedJobStops = (stops || []).filter(s => selectedJobIdsForOpti.includes(s.job_id));
      const selectedUnassigned = (unassignedStops || []).filter(us => selectedUnassignedStopIds.includes(us.id));
      const allSelectedStops = [...selectedJobStops, ...selectedUnassigned];
      const selectedStopIdsSet = new Set(allSelectedStops.map(s => s.id));

      for (const s of allSelectedStops) {
        let missingPartnerId = null;
        if (s.related_pickup_stop_id && !selectedStopIdsSet.has(parseInt(s.related_pickup_stop_id))) {
          missingPartnerId = s.related_pickup_stop_id;
        } else if (s.related_delivery_stop_id && !selectedStopIdsSet.has(parseInt(s.related_delivery_stop_id))) {
          missingPartnerId = s.related_delivery_stop_id;
        } else if (Array.isArray(stopDependencies)) {
          const depEdge = stopDependencies.find(d => (d.predecessor_stop_id === s.id && !selectedStopIdsSet.has(d.successor_stop_id)) || (d.successor_stop_id === s.id && !selectedStopIdsSet.has(d.predecessor_stop_id)));
          if (depEdge) {
            missingPartnerId = depEdge.predecessor_stop_id === s.id ? depEdge.successor_stop_id : depEdge.predecessor_stop_id;
          }
        }

        if (missingPartnerId) {
          const allStopsCombined = [...(stops || []), ...(unassignedStops || [])];
          const partnerStop = allStopsCombined.find(ps => ps.id === parseInt(missingPartnerId));
          const partnerName = partnerStop ? (partnerStop.stop_number || `STOP-${partnerStop.id}`) : `STOP-${missingPartnerId}`;
          alert(`Impossible de lancer l'optimisation croisée : le stop ${s.stop_number || `STOP-${s.id}`} est lié à ${partnerName} qui n'est pas sélectionné. Veuillez inclure les stops/missions liés ou décocher le respect des dépendances.`);
          return;
        }
      }
    }

    setIsCrossOptiLoading(true);

    const truckConfigs = selectedTruckIdsForOpti.map(id => ({
      id,
      startTime: truckTimeWindows[id]?.startTime || truckTimeWindows[id]?.shifts?.[0]?.startTime || '07:30',
      endTime: truckTimeWindows[id]?.endTime || truckTimeWindows[id]?.shifts?.[0]?.endTime || '17:00',
      driverId: truckTimeWindows[id]?.driverId || null,
      shifts: truckTimeWindows[id]?.shifts || [{ startTime: '07:30', endTime: '17:00' }]
    }));

    try {
      const res = await fetch('/api/cross-optimize-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectedJobIds: selectedJobIdsForOpti,
          selectedUnassignedStopIds: selectedUnassignedStopIds,
          selectedTruckIds: selectedTruckIdsForOpti,
          options: {
            objective: crossOptiObjective,
            flexibleStartTime: crossOptiFlexibleStart,
            enforceTimeWindows: crossOptiTimeWindows,
            enforceWeight: crossOptiEnforceWeight,
            enforceVolume: crossOptiEnforceVolume,
            enforceDependencies: crossOptiEnforceDependencies,
            dependencyMode: crossOptiEnforceDependencies ? 'strict' : 'ignore',
            maxWorkTimeMins: parseInt(crossOptiMaxHours || 8) * 60,
            enforceSkills: false,
            enforceForcedPauses: crossOptiEnforceForcedPauses,
            forcedPauses: crossOptiEnforceForcedPauses ? crossOptiForcedPauses : [],
            truckConfigs
          }
        })
      });
      const data = await res.json();
      if (res.ok && data.preview) {
        setCrossOptiPreviewData(data.preview);
        setShowCrossOptiModal(true);
      } else {
        alert(data.error || 'Échec du calcul de l\'optimisation croisée');
      }
    } catch (err) {
      console.error('Error fetching cross-optimization preview:', err);
      alert('Erreur lors du calcul de l\'optimisation croisée');
    } finally {
      setIsCrossOptiLoading(false);
    }
  };

  const handleLaunchCrossPreview = async (payload) => {
    const res = await fetch('/api/cross-optimize-preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (res.ok && data.preview) {
      setCrossOptiPreviewData(data.preview);
      setShowCrossOptiModal(true);
    } else {
      throw new Error(data.error || "Échec du calcul de l'optimisation croisée");
    }
  };

  const handleApplyCrossOptimization = async (inputJobIds, outputRoutes, unassignedStopIds) => {
    try {
      setShowCrossOptiModal(false);
      setShowCrossOptiPanel(false);
      setSelectedJobIdsForOpti([]);
      setSelectedUnassignedStopIds([]);
      setSelectedTruckIdsForOpti([]);
      setOpenJobIds([]);

      const res = await fetch('/api/apply-cross-optimization', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inputJobIds,
          outputRoutes,
          unassignedStopIds
        })
      });
      if (res.ok) {
        if (onReloadData) await onReloadData();
      } else {
        alert('Échec de l\'application de l\'optimisation croisée');
      }
    } catch (err) {
      console.error('Error applying cross-optimization:', err);
      alert('Erreur lors de l\'application de l\'optimisation croisée');
    }
  };

  // Daily totals calculation for selected date (Planned vs Real depending on job status)
  const dayTotalDistanceKm = filteredJobs.reduce((acc, j) => {
    const isStarted = j.status === 'in_progress' || j.status === 'completed' || j.status === 'done';
    const km = parseFloat(isStarted ? (j.real_length_km || j.estimated_length_km || 0) : (j.estimated_length_km || 0));
    return acc + (isNaN(km) ? 0 : km);
  }, 0);

  const dayTotalDurationSec = filteredJobs.reduce((acc, j) => {
    const isStarted = j.status === 'in_progress' || j.status === 'completed' || j.status === 'done';
    const sec = parseInt(isStarted ? (j.real_duration_sec || j.estimated_duration_sec || 0) : (j.estimated_duration_sec || 0));
    return acc + (isNaN(sec) ? 0 : sec);
  }, 0);

  const dayTotalCostChf = filteredJobs.reduce((acc, j) => {
    const isStarted = j.status === 'in_progress' || j.status === 'completed' || j.status === 'done';
    const cost = parseFloat(isStarted ? (j.real_cost || j.estimated_cost || 0) : (j.estimated_cost || 0));
    return acc + (isNaN(cost) ? 0 : cost);
  }, 0);

  let dayTotalWeightKg = 0;
  let dayTotalVolumeM3 = 0;
  let dayAssignedStopsCount = 0;
  const trucksUsedSet = new Set();
  const driversUsedSet = new Set();

  const filteredJobIds = new Set(filteredJobs.map(j => j.id));

  filteredJobs.forEach(j => {
    if (j.truck_id) trucksUsedSet.add(j.truck_id);
    if (j.driver_id) driversUsedSet.add(j.driver_id);
  });

  stops.forEach(s => {
    if (s.job_id && filteredJobIds.has(s.job_id)) {
      dayAssignedStopsCount++;
      dayTotalWeightKg += parseFloat(s.weight_kg || 0);
      dayTotalVolumeM3 += parseFloat(s.volume_m3 || 0);
    }
  });

  const handleLaunchOptimizationPreview = async (jobId, options) => {
    setOptimizingJobId(jobId);
    setIsLoadingPreview(true);
    try {
      if (onFetchOptimizationPreview) {
        const preview = await onFetchOptimizationPreview(jobId, options);
        if (preview) {
          setOptimizationPreviewData(preview);
        } else {
          alert("L'optimisation n'a pas pu être générée. Vérifiez que la tournée comporte des livraisons et que la matrice des distances est calculée.");
        }
      }
    } catch (err) {
      console.error('Optimization error:', err);
      alert("Erreur lors du calcul de l'optimisation : " + err.message);
    } finally {
      setIsLoadingPreview(false);
      setOptimizingJobId(null);
    }
  };

  const handleToggleOpenJob = (jobId) => {
    if (openJobIds.includes(jobId)) {
      setOpenJobIds(openJobIds.filter(id => id !== jobId));
    } else {
      if (openJobIds.length >= 3) {
        setOpenJobIds([...openJobIds.slice(1), jobId]);
      } else {
        setOpenJobIds([...openJobIds, jobId]);
      }
      if (typeof window !== 'undefined' && window.innerWidth <= 900) {
        setTimeout(() => {
          const pane = document.getElementById(`expedition-job-pane-${jobId}`);
          if (pane) pane.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  };

  const handleCloseOpenJob = (jobId) => {
    setOpenJobIds(openJobIds.filter(id => id !== jobId));
  };

  const handleConfirmDeleteJob = async (jobId) => {
    if (onDeleteJob) {
      await onDeleteJob(jobId);
    }
    setOpenJobIds(openJobIds.filter(id => id !== jobId));
    setConfirmDeleteJobId(null);
  };

  const handleTriggerCrossOpti = async () => {
    if (onCrossOptimize) {
      const activeJobIds = filteredJobs.map(j => j.id);
      const unassignedIds = activeUnassignedStops.map(u => u.id);
      await onCrossOptimize(activeJobIds, unassignedIds);
    }
  };

  // MANUAL SCREEN FITTING
  const handleRecenterMap = () => {
    const map = leafletMapRef.current;
    const group = markersGroupRef.current;
    if (map && group && group.getLayers().length > 0) {
      try {
        map.fitBounds(group.getBounds(), { padding: [40, 40] });
      } catch (err) {}
    }
  };

  const handleMarkerClick = (stop) => {
    if (!stop) return;
    setSelectedStopId(stop.id);

    // Pan/fly map smoothly to marker
    const lat = parseFloat(stop.latitude);
    const lng = parseFloat(stop.longitude);
    const map = leafletMapRef.current;
    if (map && !isNaN(lat) && !isNaN(lng)) {
      map.flyTo([lat, lng], 15, { duration: 0.6 });
    }

    if (stop.job_id) {
      const targetJobId = parseInt(stop.job_id);

      // Close conflicting overlays but keep unassigned deliveries drawer open for easy route building
      setShowChronologicalDrawer(false);
      setShowCrossOptiPanel(false);
      setShowRoundsBatchDrawer(false);

      // Ensure the job is open
      setOpenJobIds(prev => {
        if (prev.includes(targetJobId)) return prev;
        if (prev.length >= 3) {
          return [...prev.slice(1), targetJobId];
        }
        return [...prev, targetJobId];
      });

      // Scroll to the stop inside the open job pane with automatic retries for render latency
      const tryScrollJobStop = (attempts = 5) => {
        const stopRow = document.getElementById(`expedition-stop-row-${stop.id}`);
        const jobPane = document.getElementById(`expedition-job-pane-${targetJobId}`);

        if (jobPane) {
          jobPane.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
        }
        if (stopRow) {
          stopRow.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        } else if (attempts > 0) {
          setTimeout(() => tryScrollJobStop(attempts - 1), 60);
        }
      };
      requestAnimationFrame(() => tryScrollJobStop(5));

    } else {
      // Unassigned stop in the bucket: open bucket drawer and close others
      setShowUnassignedDrawer(true);
      setShowChronologicalDrawer(false);
      setShowDriverTimelinePanel(false);
      setShowCrossOptiPanel(false);
      setShowRoundsBatchDrawer(false);

      // Scroll to the unassigned stop card in the bucket
      const tryScrollBucketStop = (attempts = 5) => {
        const unassignedCard = document.getElementById(`expedition-unassigned-stop-${stop.id}`);
        if (unassignedCard) {
          unassignedCard.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        } else if (attempts > 0) {
          setTimeout(() => tryScrollBucketStop(attempts - 1), 60);
        }
      };
      requestAnimationFrame(() => tryScrollBucketStop(5));
    }
  };

  // Helper to check if a stop has been validated (delivered or failed)
  const isStopValidated = (stop) => {
    if (!stop) return false;
    return stop.status === 'delivered' || stop.status === 'failed';
  };

  // Helper to check if a stop is locked from drag and drop reordering
  const isStopLockedFromDrag = (stop) => {
    if (!stop) return false;
    return stop.status === 'delivered' || stop.status === 'failed' || stop.status === 'arrived' || !!stop.actual_arrival;
  };

  // DRAG AND DROP HANDLERS
  const handleStopDragStart = (e, stop, sourceJobId, index) => {
    e.stopPropagation();
    if (isStopLockedFromDrag(stop)) {
      e.preventDefault();
      return; // Validated / arrived stops cannot be moved
    }
    const item = { type: 'STOP', stop, sourceJobId, index, stopId: stop.id };
    setDragItem(item);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', JSON.stringify(item));
    } catch (err) {}
  };

  const handleUnassignedDragStart = (e, stop) => {
    e.stopPropagation();
    const item = { type: 'UNASSIGNED_STOP', stop, stopId: stop.id };
    setDragItem(item);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', JSON.stringify(item));
    } catch (err) {}
  };

  const handleDragEnd = () => {
    setDragItem(null);
    setDragOverTarget(null);
  };

  const getActiveDragPayload = (e) => {
    if (dragItem) return dragItem;
    try {
      const raw = e.dataTransfer.getData('text/plain');
      if (raw) return JSON.parse(raw);
    } catch (err) {}
    return null;
  };

  // Helper to compute next sequence index for target job
  const getNextIndexForJob = (targetJobId) => {
    const targetJob = jobs.find(j => j.id === targetJobId);
    const isTargetStarted = targetJob && (targetJob.status === 'in_progress' || targetJob.status === 'completed' || targetJob.status === 'done');
    const targetJobStops = stops.filter(s => s.job_id === targetJobId);

    if (targetJobStops.length === 0) return 1;

    if (isTargetStarted) {
      const maxReal = Math.max(...targetJobStops.map(s => parseInt(s.real_index || s.estimated_index || s.sequence_order || 0)));
      return maxReal + 1;
    } else {
      const maxEst = Math.max(...targetJobStops.map(s => parseInt(s.estimated_index || s.sequence_order || 0)));
      return maxEst + 1;
    }
  };

  // 1. Drag Over & Drop on Job Cards in Left Menu
  const handleJobDragOver = (e, jobId) => {
    e.preventDefault();
    const targetJob = jobs.find(j => j.id === jobId);
    if (targetJob && (targetJob.status === 'completed' || targetJob.status === 'done')) {
      return; // Cannot drop onto a completed job card
    }
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget({ type: 'JOB', id: jobId });
  };

  const handleJobDrop = async (e, targetJobId) => {
    e.preventDefault();
    setDragOverTarget(null);

    const targetJob = jobs.find(j => j.id === targetJobId);
    if (targetJob && (targetJob.status === 'completed' || targetJob.status === 'done')) {
      return; // Cannot drop onto a completed job card
    }

    const activeItem = getActiveDragPayload(e);
    if (!activeItem) return;

    const stopId = activeItem.stopId || activeItem.stop?.id;
    const stopObj = activeItem.stop || stops.find(s => s.id === stopId);
    if (isStopLockedFromDrag(stopObj)) return; // Validated stop cannot be moved

    const isTargetStarted = targetJob && (targetJob.status === 'in_progress');
    const nextIndex = getNextIndexForJob(targetJobId);

    if (activeItem.type === 'UNASSIGNED_STOP') {
      await onAssignStop(targetJobId, stopId);
    } else if (activeItem.type === 'STOP' && activeItem.sourceJobId !== targetJobId) {
      if (isTargetStarted) {
        await onUpdateStop(stopId, { job_id: targetJobId, sequence_order: nextIndex, real_index: nextIndex });
      } else {
        await onUpdateStop(stopId, { job_id: targetJobId, sequence_order: nextIndex, estimated_index: nextIndex });
      }
    }
    setDragItem(null);
  };

  // 2. Drag Over & Drop on "Livraisons à planifier" Bucket
  const handleBucketDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget({ type: 'BUCKET' });
  };

  const handleBucketDrop = async (e) => {
    e.preventDefault();
    setDragOverTarget(null);
    const activeItem = getActiveDragPayload(e);
    if (!activeItem) return;

    const stopId = activeItem.stopId || activeItem.stop?.id;
    const stopObj = activeItem.stop || stops.find(s => s.id === stopId);
    if (isStopLockedFromDrag(stopObj)) return; // Validated stops cannot be unassigned!

    if (activeItem.type === 'STOP') {
      await onUpdateStop(stopId, { job_id: null });
    }
    setDragItem(null);
  };

  // 3. Drag Over & Drop onto a specific Stop Row inside an Open Job
  const handleStopRowDragOver = (e, targetJobId, index) => {
    e.preventDefault();
    e.stopPropagation();

    const targetJob = jobs.find(j => j.id === targetJobId);
    if (targetJob && (targetJob.status === 'completed' || targetJob.status === 'done')) {
      return; // Cannot drop onto a completed job
    }

    const isTargetStarted = targetJob && (targetJob.status === 'in_progress');
    const targetJobStops = stops
      .filter(s => s.job_id === targetJobId)
      .sort((a, b) => {
        if (isTargetStarted) {
          return (a.real_index || a.estimated_index || a.sequence_order || 0) - (b.real_index || b.estimated_index || b.sequence_order || 0);
        } else {
          return (a.estimated_index || a.sequence_order || 0) - (b.estimated_index || b.sequence_order || 0);
        }
      });

    const lastValidatedIdx = targetJobStops.reduce((max, s, idx) => isStopLockedFromDrag(s) ? idx : max, -1);
    if (index <= lastValidatedIdx) {
      return; // Do not allow drop target on or before validated stops
    }

    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget({ type: 'STOP_ROW', jobId: targetJobId, index });
  };

  const handleStopRowDrop = async (e, targetJobId, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);

    const targetJob = jobs.find(j => j.id === targetJobId);
    if (targetJob && (targetJob.status === 'completed' || targetJob.status === 'done')) {
      return; // Cannot drop onto a completed job
    }

    const activeItem = getActiveDragPayload(e);
    if (!activeItem) return;

    const stopId = activeItem.stopId || activeItem.stop?.id;
    const stopObj = activeItem.stop || stops.find(s => s.id === stopId);
    if (isStopLockedFromDrag(stopObj)) return; // Validated stop cannot be moved

    const isTargetStarted = targetJob && (targetJob.status === 'in_progress');
    const targetJobStops = stops
      .filter(s => s.job_id === targetJobId)
      .sort((a, b) => {
        if (isTargetStarted) {
          return (a.real_index || a.estimated_index || a.sequence_order || 0) - (b.real_index || b.estimated_index || b.sequence_order || 0);
        } else {
          return (a.estimated_index || a.sequence_order || 0) - (b.estimated_index || b.sequence_order || 0);
        }
      });

    const lastValidatedIdx = targetJobStops.reduce((max, s, idx) => isStopLockedFromDrag(s) ? idx : max, -1);
    const safeDropIndex = Math.max(dropIndex, lastValidatedIdx + 1);

    if (activeItem.type === 'STOP' && activeItem.sourceJobId === targetJobId) {
      const startIndex = activeItem.index !== undefined ? activeItem.index : targetJobStops.findIndex(s => s.id === stopId);
      if (startIndex === -1 || startIndex === safeDropIndex) return;

      const newStops = [...targetJobStops];
      const item = newStops[startIndex];
      newStops.splice(startIndex, 1);
      newStops.splice(safeDropIndex, 0, item);

      const newStopIds = newStops.map(s => s.id);
      setDragItem(null);

      if (onReorderStops) {
        await onReorderStops(targetJobId, newStopIds, isTargetStarted);
      }
    } else if (activeItem.type === 'UNASSIGNED_STOP' || (activeItem.type === 'STOP' && activeItem.sourceJobId !== targetJobId)) {
      await onAssignStop(targetJobId, stopId);
      const updatedStops = [...targetJobStops];
      updatedStops.splice(safeDropIndex, 0, stopObj);
      const newStopIds = Array.from(new Set(updatedStops.map(s => s.id)));
      if (onReorderStops) {
        await onReorderStops(targetJobId, newStopIds, isTargetStarted);
      }
    }
    setDragItem(null);
  };

  // 4. Drag Over & Drop onto Job Background / Empty Area
  const handleJobPaneDragOver = (e, jobId) => {
    e.preventDefault();
    const targetJob = jobs.find(j => j.id === jobId);
    if (targetJob && (targetJob.status === 'completed' || targetJob.status === 'done')) {
      return; // Cannot drop onto a completed job
    }
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget({ type: 'JOB_PANE', jobId });
  };

  const handleJobPaneDrop = async (e, targetJobId) => {
    e.preventDefault();
    setDragOverTarget(null);

    const targetJob = jobs.find(j => j.id === targetJobId);
    if (targetJob && (targetJob.status === 'completed' || targetJob.status === 'done')) {
      return; // Cannot drop onto a completed job
    }

    const activeItem = getActiveDragPayload(e);
    if (!activeItem) return;

    const stopId = activeItem.stopId || activeItem.stop?.id;
    const stopObj = activeItem.stop || stops.find(s => s.id === stopId);
    if (isStopLockedFromDrag(stopObj)) return;

    const isTargetStarted = targetJob && (targetJob.status === 'in_progress');
    const nextIndex = getNextIndexForJob(targetJobId);

    if (activeItem.type === 'UNASSIGNED_STOP') {
      await onAssignStop(targetJobId, stopId);
    } else if (activeItem.type === 'STOP' && activeItem.sourceJobId !== targetJobId) {
      if (isTargetStarted) {
        await onUpdateStop(stopId, { job_id: targetJobId, sequence_order: nextIndex, real_index: nextIndex });
      } else {
        await onUpdateStop(stopId, { job_id: targetJobId, sequence_order: nextIndex, estimated_index: nextIndex });
      }
    }
    setDragItem(null);
  };

  const handleCreateNewJob = async () => {
    let newJob = null;
    if (onCreateJob) {
      newJob = await onCreateJob({ date: selectedDateISO });
    } else {
      const res = await fetch('/api/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: '', date: selectedDateISO })
      });
      if (res.ok) {
        newJob = await res.json();
        if (onReloadData) await onReloadData();
      }
    }
    if (newJob && newJob.id) {
      setOpenJobIds(prev => [...prev, newJob.id]);
    }
  };

  // UNIFIED LEAFLET MAP RENDERER
  useEffect(() => {
    if (!mapContainerRef.current) return;

    if (mapContainerRef.current._leaflet_id && !leafletMapRef.current) {
      mapContainerRef.current._leaflet_id = null;
    }

    if (!leafletMapRef.current) {
      try {
        const map = L.map(mapContainerRef.current, {
          center: [46.52, 6.63],
          zoom: 11,
          zoomControl: false
        });

        tileLayerRef.current = createTileLayer().addTo(map);

        markersGroupRef.current = L.featureGroup().addTo(map);
        leafletMapRef.current = map;
        setTimeout(() => {
          try { map.invalidateSize(); } catch (e) {}
        }, 250);
      } catch (err) {
        console.error('Error initializing Leaflet map:', err);
      }
    }

    const map = leafletMapRef.current;
    if (!map || !markersGroupRef.current) return;
    setTimeout(() => {
      try { map.invalidateSize(); } catch (e) {}
    }, 100);

    const group = markersGroupRef.current;
    group.clearLayers();

    // 1. PLOT HUBS (MODERN SLEEK SQUARE, MODERATELY LARGER THAN STOPS, NO EMOJI)
    (hubs || []).forEach(h => {
      const lat = parseFloat(h.latitude);
      const lng = parseFloat(h.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        const hubIcon = L.divIcon({
          className: 'hub-pin',
          html: `<div style="background:#0f172a; border:3px solid #38bdf8; border-radius:4px; width:32px; height:32px; display:flex; align-items:center; justify-content:center; box-shadow:0 4px 12px rgba(0,0,0,0.6);"><div style="background:#38bdf8; width:13px; height:13px; border-radius:2px;"></div></div>`,
          iconSize: [32, 32],
          iconAnchor: [16, 16]
        });
        L.marker([lat, lng], { icon: hubIcon })
          .bindTooltip(`<b>${h.name}</b> (Hub)`)
          .addTo(group);
      }
    });

    // 2. PLOT UNASSIGNED DELIVERIES IN BUCKET (NO PACKAGE ICON, SLEEK DUAL CIRCLE)
    activeUnassignedStops.forEach(us => {
      const lat = parseFloat(us.latitude || 46.52);
      const lng = parseFloat(us.longitude || 6.63);
      if (!isNaN(lat) && !isNaN(lng)) {
        const isSelected = selectedStopId === us.id;
        const borderColor = '#64748b'; // Sleek grey border
        const fillColor = '#64748b';   // Interior dot

        const size = isSelected ? 32 : 24;
        const innerDotSize = isSelected ? 12 : 9;

        const unassignedIcon = L.divIcon({
          className: 'unassigned-pin',
          html: `<div style="background:#0f172a; border:3px solid ${borderColor}; border-radius:50%; width:${size}px; height:${size}px; display:flex; align-items:center; justify-content:center; box-shadow:${isSelected ? '0 0 20px 5px #38bdf8, 0 4px 12px rgba(0,0,0,0.6)' : '0 2px 6px rgba(0,0,0,0.5)'}; transition:all 0.15s ease;"><div style="background:${fillColor}; border-radius:50%; width:${innerDotSize}px; height:${innerDotSize}px;"></div></div>`,
          iconSize: [size, size]
        });
        const marker = L.marker([lat, lng], { icon: unassignedIcon }).addTo(group);
        marker.bindTooltip(`<b>${us.client_name}</b> (À planifier)`);
        marker.on('click', () => handleMarkerClick(us));
      }
    });

    // 3. PLOT ASSIGNED STOPS AND POLYLINES FOR EACH JOB (50% TRANSPARENCY ON UNOPENED WHEN ANY JOB IS OPEN)
    const hasAnyOpenJobs = openJobIds.length > 0;

    filteredJobs.forEach(job => {
      const isOpen = openJobIds.includes(job.id);
      const isJobOngoing = job.status === 'in_progress';
      const isJobDone = job.status === 'completed' || job.status === 'done';
      const isJobStarted = isJobOngoing || isJobDone;
      const truck = trucks.find(t => t.id === job.truck_id);
      const baseColor = truck?.color || '#38bdf8';
      const round = rounds.find(r => r.id === job.round_id);

      // Route lines: Thinner, not dotted (solid), 50% transparency for non-open jobs when at least one job is open
      const routeOpacity = hasAnyOpenJobs ? (isOpen ? 1.0 : 0.5) : 0.85;
      const routeWeight = isOpen ? 3.5 : 2.2;
      const markerOpacity = hasAnyOpenJobs ? (isOpen ? 1.0 : 0.5) : 1.0;

      const jobStopsList = stops
        .filter(s => s.job_id === job.id)
        .sort((a, b) => {
          if (isJobStarted) {
            const idxA = a.real_index !== null && a.real_index !== undefined ? parseInt(a.real_index) : parseInt(a.estimated_index || a.sequence_order || 0);
            const idxB = b.real_index !== null && b.real_index !== undefined ? parseInt(b.real_index) : parseInt(b.estimated_index || b.sequence_order || 0);
            return idxA - idxB;
          } else {
            const idxA = a.estimated_index !== null && a.estimated_index !== undefined ? parseInt(a.estimated_index) : parseInt(a.sequence_order || 0);
            const idxB = b.estimated_index !== null && b.estimated_index !== undefined ? parseInt(b.estimated_index) : parseInt(b.sequence_order || 0);
            return idxA - idxB;
          }
        });

      const polylineCoords = [];
      const valhallaPolyline = job.planned_polyline;

      const startHub = hubs.find(h => h.id === job?.start_hub_id) || hubs[0];
      if (startHub && !isNaN(parseFloat(startHub.latitude))) {
        polylineCoords.push([parseFloat(startHub.latitude), parseFloat(startHub.longitude)]);
      }

      jobStopsList.forEach((stop, idx) => {
        const lat = parseFloat(stop.latitude);
        const lng = parseFloat(stop.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          polylineCoords.push([lat, lng]);

          const isSelected = selectedStopId === stop.id;
          const stopDisplayNum = isJobStarted
            ? (stop.real_index !== null && stop.real_index !== undefined ? stop.real_index : (stop.estimated_index || idx + 1))
            : (stop.estimated_index !== null && stop.estimated_index !== undefined ? stop.estimated_index : (idx + 1));

          // Outer circle: Truck color
          const borderColor = truck?.color || '#38bdf8';

          // Inner circle: Swaps between status color and job's truck color
          let fillColor = '#38bdf8';
          let textColor = '#0f172a';

          if (markerColorMode === 'truck') {
            fillColor = truck?.color || '#38bdf8';
            textColor = '#ffffff';
          } else {
            if (stop.status === 'delivered') {
              fillColor = '#22c55e';
              textColor = '#ffffff';
            } else if (stop.status === 'failed') {
              fillColor = '#ef4444';
              textColor = '#ffffff';
            } else if (job.status === 'planned') {
              fillColor = '#ffffff';
              textColor = '#0f172a';
            } else {
              fillColor = '#38bdf8';
              textColor = '#0f172a';
            }
          }
          
          const size = isSelected ? 34 : 26;
          const innerSize = isSelected ? 22 : 17;

          const stopIcon = L.divIcon({
            className: `stop-pin ${isOpen ? 'open' : 'unopened'}`,
            html: `<div style="background:#0f172a; border:3px solid ${borderColor}; border-radius:50%; width:${size}px; height:${size}px; display:flex; align-items:center; justify-content:center; box-shadow:${isSelected ? '0 0 20px 5px #38bdf8, 0 4px 12px rgba(0,0,0,0.6)' : '0 3px 8px rgba(0,0,0,0.5)'}; transition:all 0.15s ease;"><div style="background:${fillColor}; color:${textColor}; border-radius:50%; width:${innerSize}px; height:${innerSize}px; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:${isSelected ? '11px' : '9px'};">${stopDisplayNum}</div></div>`,
            iconSize: [size, size]
          });

          const marker = L.marker([lat, lng], { icon: stopIcon, opacity: markerOpacity }).addTo(group);
          marker.bindTooltip(`<b>${stop.client_name}</b> (${job.display_name || round?.name || 'Tournée'})`);
          marker.on('click', () => handleMarkerClick(stop));
        }
      });

      const endHub = hubs.find(h => h.id === job?.end_hub_id) || startHub;
      if (endHub && !isNaN(parseFloat(endHub.latitude))) {
        polylineCoords.push([parseFloat(endHub.latitude), parseFloat(endHub.longitude)]);
      }

      const renderCoords = (valhallaPolyline && valhallaPolyline.length > 0) ? valhallaPolyline : polylineCoords;

      // 1. PLANNED ROUTE TRACE
      // - When job is ongoing: dashed line
      // - When job is planned: solid line
      // - When job is done: hidden (only real trace visible)
      if (!isJobDone && renderCoords.length > 1) {
        try {
          const plannedPolyline = L.polyline(renderCoords, {
            color: baseColor,
            weight: isJobOngoing ? (isOpen ? 4 : 3.2) : routeWeight,
            opacity: isJobOngoing ? 0.9 : routeOpacity,
            dashArray: isJobOngoing ? '8, 9' : null
          }).addTo(group);

          const tooltipLabel = isJobOngoing
            ? `<b>${job.display_name || round?.name || 'Tournée'}</b> (Itinéraire théorique / planifié)`
            : `<b>${job.display_name || round?.name || 'Tournée'}</b> (Cliquer pour ouvrir)`;
          plannedPolyline.bindTooltip(tooltipLabel);
          plannedPolyline.on('click', () => {
            handleToggleOpenJob(job.id);
          });
        } catch (err) {}
      }

      // 2. REAL ROUTE TRACE (for ongoing or completed jobs)
      // Full (solid) line of the same color as the job
      if (isJobStarted) {
        const rawGeo = geohistoryByJobId[job.id] || [];
        const validGeo = rawGeo.filter(p => {
          const lat = parseFloat(p.latitude);
          const lng = parseFloat(p.longitude);
          if (isNaN(lat) || isNaN(lng)) return false;
          if (p.accuracy !== null && p.accuracy !== undefined) {
            const acc = parseFloat(p.accuracy);
            if (!isNaN(acc) && acc > 50) return false;
          }
          return true;
        });

        let realTraceCoords = [];
        if (validGeo.length > 1) {
          realTraceCoords = validGeo.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]);
        } else if (validGeo.length === 1) {
          if (startHub && !isNaN(parseFloat(startHub.latitude))) {
            realTraceCoords = [
              [parseFloat(startHub.latitude), parseFloat(startHub.longitude)],
              [parseFloat(validGeo[0].latitude), parseFloat(validGeo[0].longitude)]
            ];
          }
        } else if (isJobDone) {
          // Fallback if completed without recorded GPS breadcrumbs: connect startHub -> delivered stops -> endHub
          const deliveredStops = jobStopsList.filter(s => s.status === 'delivered' || s.status === 'failed' || !!s.actual_arrival);
          if (deliveredStops.length > 0) {
            const coords = [];
            if (startHub && !isNaN(parseFloat(startHub.latitude))) {
              coords.push([parseFloat(startHub.latitude), parseFloat(startHub.longitude)]);
            }
            deliveredStops.forEach(s => {
              const lat = parseFloat(s.latitude);
              const lng = parseFloat(s.longitude);
              if (!isNaN(lat) && !isNaN(lng)) coords.push([lat, lng]);
            });
            if (endHub && !isNaN(parseFloat(endHub.latitude))) {
              coords.push([parseFloat(endHub.latitude), parseFloat(endHub.longitude)]);
            }
            if (coords.length > 1) {
              realTraceCoords = coords;
            }
          }
        }

        if (realTraceCoords.length > 1) {
          try {
            const realPolyline = L.polyline(realTraceCoords, {
              color: baseColor, // OF THE SAME COLOR AS THE JOB
              weight: isOpen ? 4 : 3.2,
              opacity: isOpen ? 1.0 : (hasAnyOpenJobs ? 0.6 : 0.95),
              dashArray: null // FULL (solid)
            }).addTo(group);

            const tooltipText = isJobOngoing
              ? `<b>${job.display_name || round?.name || 'Tournée'}</b> (Tracé réel GPS en direct)`
              : `<b>${job.display_name || round?.name || 'Tournée'}</b> (Tracé réel réalisé)`;
            realPolyline.bindTooltip(tooltipText);
            realPolyline.on('click', () => {
              handleToggleOpenJob(job.id);
            });
          } catch (err) {}
        }

        // 3. CURRENT POSITION MARKER (ONLY FOR ONGOING JOBS)
        // "with a small marker for the current position so we can see what's happening live"
        if (isJobOngoing && validGeo.length > 0) {
          const latest = validGeo[validGeo.length - 1];
          const lat = parseFloat(latest.latitude);
          const lng = parseFloat(latest.longitude);
          if (!isNaN(lat) && !isNaN(lng)) {
            const headingDeg = latest.heading !== null && latest.heading !== undefined && !isNaN(latest.heading) ? parseFloat(latest.heading) : null;
            const timeStr = latest.recorded_at ? new Date(latest.recorded_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
            const speedStr = latest.speed !== null && latest.speed !== undefined && !isNaN(latest.speed) ? `${Math.round(latest.speed)} km/h` : 'À l\'arrêt';
            const driverObj = drivers.find(d => d.id === job.driver_id);
            const driverName = driverObj ? `${driverObj.first_name} ${driverObj.last_name || ''}`.trim() : 'Chauffeur';

            const pulseHtml = `
              <div style="position:relative; width:26px; height:26px; display:flex; align-items:center; justify-content:center;">
                <div style="position:absolute; width:24px; height:24px; border-radius:50%; background:${baseColor}; opacity:0.4; animation:live-pulse 2s infinite ease-in-out;"></div>
                <div style="position:relative; width:16px; height:16px; border-radius:50%; background:${baseColor}; border:2px solid #ffffff; box-shadow:0 0 10px ${baseColor}, 0 2px 6px rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; color:#ffffff; font-size:9px; font-weight:900;">
                  ${headingDeg !== null ? `<span style="transform:rotate(${headingDeg}deg); display:inline-block; line-height:1;">▲</span>` : '●'}
                </div>
              </div>
            `;

            const liveIcon = L.divIcon({
              className: 'live-position-pin',
              html: pulseHtml,
              iconSize: [26, 26],
              iconAnchor: [13, 13]
            });

            const liveMarker = L.marker([lat, lng], {
              icon: liveIcon,
              zIndexOffset: 1200
            }).addTo(group);

            liveMarker.bindTooltip(`
              <div style="font-family:sans-serif; padding:3px; font-size:11px;">
                <div style="font-weight:700; color:#0f172a; margin-bottom:2px;">📍 En direct : ${job.display_name || round?.name || 'Tournée'}</div>
                <div style="color:#64748b;">${truck?.name || 'Véhicule'} • ${driverName}</div>
                <div style="color:#0284c7; font-weight:600; margin-top:2px;">Vitesse : ${speedStr} • ${timeStr}</div>
              </div>
            `);

            liveMarker.on('click', () => {
              handleToggleOpenJob(job.id);
            });
          }
        }
      }
    });

    return () => {
      // Retain Leaflet map instance for smooth performance (do not destroy map instance)
    };
  }, [hubs, openJobIds, stops, jobs, activeUnassignedStops, trucks, selectedStopId, rounds, markerColorMode, geohistoryByJobId, drivers]);

  useEffect(() => {
    setOpenJobIds(prev => prev.filter(id => jobs.some(j => j.id === id)));
  }, [jobs]);

  const validOpenJobIds = openJobIds
    .filter(id => jobs.some(j => j.id === id))
    .sort((a, b) => {
      const jobA = jobs.find(j => j.id === a);
      const jobB = jobs.find(j => j.id === b);
      const nameA = getJobDisplayName(jobA);
      const nameB = getJobDisplayName(jobB);
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
  const hasSideDrawer = showUnassignedDrawer || showChronologicalDrawer || showRoundsBatchDrawer || showDriverTimelinePanel;
  const sideDrawerWidth = showDriverTimelinePanel ? 820 : 440;
  const leftPanelWidth = `${370 + (validOpenJobIds.length * 440) + (hasSideDrawer ? sideDrawerWidth : 0)}px`;



  const sortedChronologicalStops = [...stops].filter(s => {
    if (!isStopOnSelectedDate(s)) return false;
    if (showAllRealtimeStops) return true;
    const isValidated = s.status === 'delivered' || s.status === 'failed' || !!s.real_departure || (!!s.actual_arrival && isStopValidated(s));
    return isValidated;
  }).sort((a, b) => {
    const isBucketA = a.job_id === null || a.job_id === undefined;
    const isBucketB = b.job_id === null || b.job_id === undefined;
    if (isBucketA && !isBucketB) return 1; // Bucket deliveries at the bottom
    if (!isBucketA && isBucketB) return -1;
    if (isBucketA && isBucketB) return (a.id || 0) - (b.id || 0);

    const isValA = a.status === 'delivered' || a.status === 'failed' || !!a.real_departure || (!!a.actual_arrival && isStopValidated(a));
    const isValB = b.status === 'delivered' || b.status === 'failed' || !!b.real_departure || (!!b.actual_arrival && isStopValidated(b));
    if (isValA && !isValB) return -1; // Validated stops first
    if (!isValA && isValB) return 1;

    const getSec = (s) => {
      if (s.real_departure) {
        const timeStr = formatLocalTimeHHMM(s.real_departure);
        if (timeStr && timeStr.includes(':') && timeStr !== '--:--') return timeStringToSec(timeStr);
      }
      if (s.actual_arrival) {
        const timeStr = formatLocalTimeHHMM(s.actual_arrival);
        if (timeStr && timeStr.includes(':') && timeStr !== '--:--') return timeStringToSec(timeStr);
      }
      if (s.estimated_departure) {
        return timeStringToSec(s.estimated_departure.slice(0, 5));
      }
      if (s.estimated_arrival) {
        return timeStringToSec(s.estimated_arrival.slice(0, 5)) + (parseInt(s.service_time_mins || 10) * 60);
      }
      return 28800; // 08:00 default
    };
    return getSec(b) - getSec(a); // Most recent at top
  });

  return (
    <div className="expedition-container">
      {/* LEFT MANAGEMENT AREA */}
      <div className="expedition-left-panel" style={{ width: leftPanelWidth, maxWidth: '85vw' }}>
        
        {/* COLLAPSED / EXPANDED JOBS MENU PANEL (FIXED WIDTH 370px, NO SIZE CHANGE ON TABS) */}
        <div className="expedition-jobs-menu" style={{ width: '370px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid #334155', flexShrink: 0 }}>
          {/* Header Bar */}
          <div className="overlay-card">
            <div className="date-controls" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div
                className="date-badge"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '7px',
                  cursor: 'pointer',
                  padding: '5px 10px',
                  userSelect: 'none'
                }}
                onClick={() => setShowDatePicker(true)}
                title="Cliquer pour choisir une date ou une plage de dates"
              >
                <Calendar size={14} style={{ color: '#38bdf8', flexShrink: 0 }} />
                <span style={{ fontWeight: '800', fontSize: '0.82rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {getHeaderDateBadgeText()}
                </span>
              </div>

              <button className="icon-btn-header" title="Chercher une livraison" onClick={() => setShowSearchInput(!showSearchInput)}>
                <Search size={15} />
              </button>

              <button
                className="icon-btn-header"
                title="Suivi en temps réel"
                onClick={() => {
                  const next = !showChronologicalDrawer;
                  setShowChronologicalDrawer(next);
                  if (next) {
                    setShowUnassignedDrawer(false);
                    setShowDriverTimelinePanel(false);
                    setShowCrossOptiPanel(false);
                    setShowMultiMissionPanel(false);
                    setShowRoundsBatchDrawer(false);
                  }
                }}
                style={{ color: showChronologicalDrawer ? '#38bdf8' : undefined }}
              >
                <Activity size={15} />
              </button>

              <button className="icon-btn-header" title="Imprimer toute la journée">
                <Printer size={15} />
              </button>

              <button
                className="icon-btn-header"
                title="Optimisation croisée VROOM"
                onClick={() => {
                  setShowCrossOptiOptionsModal(true);
                }}
                style={{ color: showCrossOptiOptionsModal ? '#38bdf8' : undefined }}
              >
                <Zap size={15} />
              </button>

              <button
                className="icon-btn-header"
                title="Vue Chronologique par Chauffeur (Timeline)"
                onClick={() => {
                  const next = !showDriverTimelinePanel;
                  setShowDriverTimelinePanel(next);
                  if (next) {
                    setShowUnassignedDrawer(false);
                    setShowChronologicalDrawer(false);
                    setShowCrossOptiPanel(false);
                    setOpenJobIds([]);
                  }
                }}
                style={{ color: showDriverTimelinePanel ? '#38bdf8' : undefined }}
              >
                <GitCommit size={15} />
              </button>
            </div>

            {showSearchInput && (
              <div style={{ marginTop: '6px' }}>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Chercher..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
            )}

            {/* DAILY TOTAL METRICS SUMMARY BAR (2 ROWS IN SAME ENCADRÉ) */}
            <div style={{
              background: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '6px',
              padding: '8px 10px',
              marginTop: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              fontSize: '0.76rem'
            }}>
              {/* ROW 1: DISTANCE (GREEN NAV), DURATION (BLUE CLOCK), COST (RED DOLLAR) - EVENLY SPACED & CENTERED */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Distance totale du jour">
                  <Navigation size={13} color="#22c55e" />
                  <b style={{ color: '#ffffff' }}>{dayTotalDistanceKm.toFixed(1)} km</b>
                </span>

                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Temps total du jour">
                  <Clock size={13} color="#38bdf8" />
                  <b style={{ color: '#ffffff' }}>{formatDurationSec(dayTotalDurationSec)}</b>
                </span>

                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Coût total du jour">
                  <DollarSign size={13} color="#ef4444" />
                  <b style={{ color: '#ffffff' }}>{dayTotalCostChf.toFixed(2)} CHF</b>
                </span>
              </div>

              {/* ROW 2: 6 EVENLY SPACED METRICS (kg, volume, drivers, trucks, jobs, stops) - NON-BOLD */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 1fr)',
                alignItems: 'center',
                justifyItems: 'center',
                paddingTop: '6px',
                borderTop: '1px solid #1e293b',
                fontSize: '0.78rem',
                fontWeight: '400',
                color: '#ffffff'
              }}>
                {/* 1. kg / t */}
                <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }} title="Poids total transporté aujourd'hui">
                  <span style={{ color: '#ffffff', fontSize: '0.71rem' }}>
                    {dayTotalWeightKg >= 1000 ? `${(dayTotalWeightKg / 1000).toFixed(1)} t` : `${Math.round(dayTotalWeightKg)} kg`}
                  </span>
                </span>

                {/* 2. volume */}
                <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }} title="Volume total transporté aujourd'hui">
                  <span style={{ color: '#ffffff', fontSize: '0.71rem' }}>
                    {(Math.round(dayTotalVolumeM3 * 10) / 10).toFixed(1)} m³
                  </span>
                </span>

                {/* 3. drivers (Blue User Icon) */}
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Chauffeurs attribués aujourd'hui">
                  <User size={14} color="#38bdf8" />
                  <span style={{ color: '#ffffff' }}>{driversUsedSet.size}</span>
                </span>

                {/* 4. trucks (Red Truck Icon) */}
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Camions utilisés aujourd'hui">
                  <Truck size={14} color="#ef4444" />
                  <span style={{ color: '#ffffff' }}>{trucksUsedSet.size}</span>
                </span>

                {/* 5. jobs (Green Route Icon) */}
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Nombre de tournées (missions) aujourd'hui">
                  <Route size={14} color="#22c55e" />
                  <span style={{ color: '#ffffff' }}>{filteredJobs.length}</span>
                </span>

                {/* 6. stops (White Circle + Label to the Right) */}
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Nombre total de stops attribués aujourd'hui">
                  <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#ffffff', display: 'inline-block', flexShrink: 0 }}></span>
                  <span style={{ color: '#ffffff' }}>{dayAssignedStopsCount}</span>
                </span>
              </div>
            </div>

            {/* ACTION BAR: RELOCATED JUST ABOVE "À PLANIFIER" AND BELOW DAILY STATS RECAP */}
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              {/* RED TRASH CAN BUTTON ON THE LEFT */}
              <button
                className="icon-btn-header"
                title="Supprimer toutes les missions non démarrées de la journée"
                onClick={() => setShowDeleteAllModal(true)}
                style={{
                  background: 'rgba(239, 68, 68, 0.15)',
                  color: '#ef4444',
                  border: '1px solid #ef4444',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'all 0.15s ease'
                }}
              >
                <Trash2 size={15} />
              </button>

              {/* DASHED NOUVELLE MISSION BUTTON IN MIDDLE */}
              <button
                className="btn-new-job-dashed"
                onClick={handleCreateNewJob}
                style={{
                  flex: 1,
                  height: '32px',
                  marginTop: 0,
                  background: '#0f172a',
                  border: '1px dashed #334155',
                  color: '#cbd5e1',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  fontSize: '0.8rem',
                  fontWeight: '600'
                }}
              >
                <Plus size={15} />
                <span>Nouvelle mission</span>
              </button>

              {/* DYNAMIC BLUE BATCH MISSIONS BUTTON ON THE RIGHT */}
              <button
                className="icon-btn-header"
                title="Créer plusieurs missions"
                onClick={() => {
                  setShowRoundsBatchDrawer(!showRoundsBatchDrawer);
                  setShowUnassignedDrawer(false);
                  setShowChronologicalDrawer(false);
                  setShowCrossOptiPanel(false);
                }}
                style={{
                  background: showRoundsBatchDrawer ? '#38bdf8' : '#0f172a',
                  color: showRoundsBatchDrawer ? '#0f172a' : '#38bdf8',
                  border: '1px solid #38bdf8',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'all 0.15s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
                  <Plus size={10} style={{ fontWeight: '800' }} />
                  <Route size={14} />
                </div>
              </button>
            </div>

            {/* Clickable & Drop Target "Livraisons à planifier" Bucket Button */}
            <div
              onDragOver={handleBucketDragOver}
              onDrop={handleBucketDrop}
              style={{
                background: dragOverTarget?.type === 'BUCKET' ? 'rgba(245, 158, 11, 0.35)' : showUnassignedDrawer ? 'rgba(245, 158, 11, 0.25)' : '#1e293b',
                border: dragOverTarget?.type === 'BUCKET' ? '2px dashed #f59e0b' : '1px solid #f59e0b',
                padding: '7px 12px',
                borderRadius: '6px',
                marginTop: '8px',
                fontSize: '0.8rem',
                display: 'flex',
                justify: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onClick={() => {
                setShowUnassignedDrawer(!showUnassignedDrawer);
                setShowChronologicalDrawer(false);
                setShowDriverTimelinePanel(false);
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Package size={16} color="#f59e0b" />
                <span style={{ fontWeight: '700', color: '#ffffff' }}>À planifier</span>
              </div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginLeft: 'auto', flexShrink: 0, fontSize: '0.78rem', fontWeight: '400', color: '#ffffff' }}>
                <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#f59e0b', display: 'inline-block', flexShrink: 0 }}></span>
                <span>{activeUnassignedStops.length}</span>
              </span>
            </div>
          </div>

          {/* Missions List */}
          <div className="missions-list" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingRight: '4px' }}>
            {filteredJobs.map(job => {
              const isOpen = openJobIds.includes(job.id);
              const isJobHovered = dragOverTarget?.type === 'JOB' && dragOverTarget.id === job.id;
              const jobStopsList = stops.filter(s => s.job_id === job.id);
              const round = rounds.find(r => r.id === job.round_id);
              const truckInfo = trucks.find(t => t.id === job.truck_id);
              const payloadStats = calculateTruckPeakPayload(truckInfo, jobStopsList);

              const isJobStarted = job.status === 'in_progress' || job.status === 'completed' || job.status === 'done';

              const estimatedKm = parseFloat(job.estimated_length_km || 0);
              const estimatedSec = parseInt(job.estimated_duration_sec || 0);
              const estimatedCost = parseFloat(job.estimated_cost || 0);

              let cardHeaderBg = '#1e293b';
              if (job.status === 'in_progress') {
                cardHeaderBg = 'rgba(56, 189, 248, 0.25)';
              } else if (job.status === 'completed' || job.status === 'done') {
                cardHeaderBg = 'rgba(34, 197, 94, 0.25)';
              }

              let cardBodyBg = '#1e293b';
              if (isJobHovered) {
                cardBodyBg = 'rgba(56, 189, 248, 0.25)';
              } else if (isOpen) {
                cardBodyBg = 'rgba(56, 189, 248, 0.15)';
              }

              // Circle dot color rules:
              // Red if no vehicle is selected; White if planned; Blue if ongoing; Green if done.
              let countDotColor = '#ffffff';
              if (!job.truck_id) {
                countDotColor = '#ef4444';
              } else if (job.status === 'in_progress') {
                countDotColor = '#38bdf8';
              } else if (job.status === 'completed' || job.status === 'done') {
                countDotColor = '#22c55e';
              }

              const assignedDriver = drivers.find(d => d.id === job.driver_id);

              return (
                <div
                  key={job.id}
                  onDragOver={e => handleJobDragOver(e, job.id)}
                  onDrop={e => handleJobDrop(e, job.id)}
                  style={{
                    borderRadius: '6px',
                    border: isOpen ? '1px solid #38bdf8' : isJobHovered ? '2px dashed #38bdf8' : '1px solid #334155',
                    background: cardBodyBg,
                    overflow: 'hidden',
                    marginBottom: '4px',
                    flexShrink: 0,
                    cursor: isJobStarted ? 'default' : 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onClick={() => handleToggleOpenJob(job.id)}
                >
                  {/* CARD HEADER WITH TRUCK COLOR LEFT BAR & RIGHT-ALIGNED COUNT BADGE */}
                  <div
                    style={{
                      background: cardHeaderBg,
                      padding: '6px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      justify: 'space-between',
                      borderLeft: `4px solid ${truckInfo?.color || '#ef4444'}`
                    }}
                  >
                    <span style={{ fontWeight: '700', fontSize: '0.82rem', color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {job.display_name || (round?.name || 'Nouvelle mission')}
                    </span>

                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginLeft: 'auto', flexShrink: 0, fontSize: '0.78rem', fontWeight: '400', color: '#ffffff' }}>
                      <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: countDotColor, display: 'inline-block', flexShrink: 0 }}></span>
                      <span>{jobStopsList.length}</span>
                    </span>
                  </div>

                  {/* CARD BODY CONTENT */}
                  <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    
                    {/* ROW 1: DRIVER (LEFT) & METRICS DIST/TIME/COST (RIGHT IN SMALL) */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: 0 }} onClick={e => e.stopPropagation()}>
                        <User size={13} color="#38bdf8" style={{ flexShrink: 0 }} />
                        {isJobStarted ? (
                          <span style={{ fontSize: '0.68rem', color: '#ffffff', fontWeight: '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {assignedDriver ? `${assignedDriver.first_name} ${assignedDriver.last_name}` : '--'}
                          </span>
                        ) : (
                          <select
                            className="table-input"
                            style={{
                              width: '100%',
                              padding: '1px 4px',
                              fontSize: '0.68rem',
                              background: '#0f172a',
                              color: '#ffffff',
                              border: !job.driver_id ? '1px solid #f59e0b' : '1px solid #334155',
                              boxShadow: !job.driver_id ? '0 0 4px rgba(245, 158, 11, 0.25)' : 'none',
                              fontWeight: '400'
                            }}
                            value={job.driver_id || ''}
                            onChange={e => onUpdateJob(job.id, { driver_id: e.target.value ? parseInt(e.target.value) : null })}
                          >
                            <option value="">--</option>
                            {drivers.map(d => (
                              <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.66rem', color: '#cbd5e1', flexShrink: 0 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }} title="Distance estimée">
                          <Navigation size={11} color="#22c55e" />
                          <span>{job.truck_id ? `${estimatedKm.toFixed(1)} km` : '--'}</span>
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }} title="Durée estimée">
                          <Clock size={11} color="#38bdf8" />
                          <span>{job.truck_id ? formatDurationSec(estimatedSec) : '--'}</span>
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }} title="Coût estimé">
                          <DollarSign size={11} color="#ef4444" />
                          <span>{job.truck_id ? `${estimatedCost.toFixed(2)} CHF` : '--'}</span>
                        </span>
                      </div>
                    </div>

                    {/* ROW 2: TRUCK (LEFT) & TRUCK CAPACITIES BADGE (RIGHT) */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: 0 }} onClick={e => e.stopPropagation()}>
                        <Truck size={13} color="#ef4444" style={{ flexShrink: 0 }} />
                        {isJobStarted ? (
                          <span style={{ fontSize: '0.68rem', color: '#ffffff', fontWeight: '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {truckInfo?.plate || '--'}
                          </span>
                        ) : (
                          <select
                            className="table-input"
                            style={{
                              width: '100%',
                              padding: '1px 4px',
                              fontSize: '0.68rem',
                              background: '#0f172a',
                              color: '#ffffff',
                              border: !job.truck_id ? '1px solid #f59e0b' : '1px solid #334155',
                              boxShadow: !job.truck_id ? '0 0 4px rgba(245, 158, 11, 0.25)' : 'none',
                              fontWeight: '400'
                            }}
                            value={job.truck_id || ''}
                            onChange={e => onUpdateJob(job.id, { truck_id: e.target.value ? parseInt(e.target.value) : null })}
                          >
                            <option value="">--</option>
                            {trucks.map(t => (
                              <option key={t.id} value={t.id}>{t.plate}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      {/* Truck Payload Capacities Badge */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '0.65rem',
                          fontWeight: '400',
                          background: payloadStats.isOverweight || payloadStats.isOvervolume ? 'rgba(239, 68, 68, 0.25)' : 'transparent',
                          border: payloadStats.isOverweight || payloadStats.isOvervolume ? '1px solid #ef4444' : 'none',
                          padding: '1px 5px',
                          borderRadius: '4px',
                          flexShrink: 0
                        }}
                        title={`Charge crête durant la tournée: ${payloadStats.peakWeight} / ${payloadStats.maxWeight} kg | ${payloadStats.peakVolume} / ${payloadStats.maxVolume} m³`}
                      >
                        <span style={{ color: payloadStats.isOverweight ? '#ef4444' : '#ffffff' }}>
                          {formatWeightPair(payloadStats.peakWeight, payloadStats.maxWeight)}
                        </span>
                        <span style={{ color: '#64748b' }}>|</span>
                        <span style={{ color: payloadStats.isOvervolume ? '#ef4444' : '#ffffff' }}>
                          {payloadStats.peakVolume}/{payloadStats.maxVolume}m³
                        </span>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* BATCH CREATE MISSIONS BY ROUND DRAWER (WIDER 440px) */}
        {showRoundsBatchDrawer && (
          <div className="left-detail-pane" style={{ width: '440px', flexShrink: 0, background: '#182030', borderLeft: '1px solid #334155', display: 'flex', flexDirection: 'column' }}>
            {/* Drawer Header */}
            <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: '0.95rem', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '800' }}>
                  <Route size={18} color="#38bdf8" /> Créer plusieurs missions
                </h3>
                <button onClick={() => setShowRoundsBatchDrawer(false)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}>
                  <X size={18} />
                </button>
              </div>

              {/* Master Action Button: Créer <x> missions avec des stops */}
              <button
                className="btn-action"
                disabled={roundsWithAvailableStops.length === 0 || isBatchCreating}
                onClick={handleCreateAllMissionsWithStops}
                title="Créer une mission pour chaque tournée ayant des arrêts disponibles et y ajouter les arrêts"
                style={{
                  width: '100%',
                  background: roundsWithAvailableStops.length > 0
                    ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'
                    : '#0f172a',
                  color: roundsWithAvailableStops.length > 0 ? '#ffffff' : '#64748b',
                  border: roundsWithAvailableStops.length > 0 ? '1px solid #38bdf8' : '1px solid #334155',
                  borderRadius: '6px',
                  padding: '9px 12px',
                  fontSize: '0.82rem',
                  fontWeight: '700',
                  cursor: roundsWithAvailableStops.length === 0 || isBatchCreating ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: roundsWithAvailableStops.length > 0 ? '0 2px 10px rgba(2, 132, 199, 0.35)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                <Sparkles size={15} color={roundsWithAvailableStops.length > 0 ? '#ffffff' : '#64748b'} />
                <span>
                  {isBatchCreating
                    ? 'Création des missions en cours...'
                    : `Créer ${roundsWithAvailableStops.length} ${roundsWithAvailableStops.length > 1 ? 'missions' : 'mission'} avec des stops`}
                </span>
                {roundsWithAvailableStops.length > 0 && !isBatchCreating && (
                  <span style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: '#ffffff',
                    padding: '1px 6px',
                    borderRadius: '10px',
                    fontSize: '0.72rem',
                    fontWeight: '800'
                  }}>
                    {totalAvailableStopsInRounds} {totalAvailableStopsInRounds > 1 ? 'stops' : 'stop'}
                  </span>
                )}
              </button>
            </div>

            {/* Rounds List View */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {rounds.map(r => {
                const count = activeUnassignedStops.filter(us => {
                  const client = clients.find(c => c.id === us.client_id);
                  return (us.default_round_id || client?.default_round_id) === r.id;
                }).length;

                return (
                  <div
                    key={r.id}
                    style={{
                      background: '#0f172a',
                      border: '1px solid #334155',
                      borderLeft: `4px solid ${r.color || '#22c55e'}`,
                      borderRadius: '6px',
                      padding: '10px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justify: 'space-between',
                      gap: '12px'
                    }}
                  >
                    {/* Round Name & Code directly below in small grey */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ color: '#ffffff', fontSize: '0.88rem', display: 'block', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {r.name}
                      </strong>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '1px' }}>
                        {r.code}
                      </div>
                    </div>

                      {/* Action Buttons Stacked One Above Another */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                        <button
                          className="btn-action"
                          style={{ padding: '5px 10px', fontSize: '0.74rem', background: '#1e293b', color: '#cbd5e1', border: '1px solid #334155' }}
                          onClick={async () => {
                            try {
                              const res = await fetch(`/api/rounds/${r.id}/create-job`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  include_bucket_stops: false,
                                  date: selectedDateISO,
                                  start_date: dateFilter?.startDate,
                                  end_date: dateFilter?.endDate,
                                  all: dateFilter?.isAll
                                })
                              });
                              if (res.ok) {
                                const newJob = await res.json();
                                if (newJob?.id) setOpenJobIds(prev => [...prev, newJob.id]);
                                if (onReloadData) await onReloadData();
                              }
                            } catch (err) {
                              console.error('Error creating empty job from round:', err);
                            }
                          }}
                        >
                          Créer mission vide
                        </button>

                        {count > 0 && (
                          <button
                            className="btn-action"
                            style={{ padding: '5px 10px', fontSize: '0.74rem', background: '#1e293b', color: '#cbd5e1', border: '1px solid #334155' }}
                            onClick={async () => {
                              try {
                                const res = await fetch(`/api/rounds/${r.id}/create-job`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    include_bucket_stops: true,
                                    date: selectedDateISO,
                                    start_date: dateFilter?.startDate,
                                    end_date: dateFilter?.endDate,
                                    all: dateFilter?.isAll
                                  })
                                });
                                if (res.ok) {
                                  const newJob = await res.json();
                                  if (newJob?.id) setOpenJobIds(prev => [...prev, newJob.id]);
                                  if (onReloadData) await onReloadData();
                                }
                              } catch (err) {
                                console.error('Error creating job with stops:', err);
                              }
                            }}
                          >
                            Créer et ajouter {count} {count > 1 ? 'stops' : 'stop'}
                          </button>
                        )}
                      </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* UNASSIGNED STOPS DRAWER */}
        {showUnassignedDrawer && (
          <div className="left-detail-pane" style={{ width: '440px', flexShrink: 0, background: '#182030', borderLeft: '1px solid #334155', display: 'flex', flexDirection: 'column' }}>
            {/* Drawer Header with Title & Minimalist Single-Line Filter/Sort Toolbar */}
            <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0, position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: '0.92rem', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '800' }}>
                  <Package size={17} color="#f59e0b" /> Livraisons à planifier ({displayedUnassignedStops.length}{displayedUnassignedStops.length !== activeUnassignedStops.length ? ` / ${activeUnassignedStops.length}` : ''})
                </h3>
                <button onClick={() => setShowUnassignedDrawer(false)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}>
                  <X size={18} />
                </button>
              </div>

              {/* Single Minimalist Line: Round Multiselect Dropdown (Left) + Sort Dropdown & Order Toggle (Right) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between', position: 'relative' }}>
                {/* Round Multiselector Dropdown */}
                <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                  <button
                    type="button"
                    onClick={() => setShowRoundFilterDropdown(!showRoundFilterDropdown)}
                    style={{
                      width: '100%',
                      background: unassignedSelectedRoundIds.length > 0 ? 'rgba(56, 189, 248, 0.15)' : '#0f172a',
                      color: unassignedSelectedRoundIds.length > 0 ? '#38bdf8' : '#cbd5e1',
                      border: unassignedSelectedRoundIds.length > 0 ? '1px solid #38bdf8' : '1px solid #334155',
                      borderRadius: '6px',
                      padding: '4px 8px',
                      fontSize: '0.72rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '4px'
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {unassignedSelectedRoundIds.length === 0
                        ? 'Tournées: Toutes'
                        : unassignedSelectedRoundIds.length === 1
                        ? `Tournée: ${rounds.find(r => r.id === unassignedSelectedRoundIds[0])?.name || 'Sélection'}`
                        : `Tournées: ${unassignedSelectedRoundIds.length} sélectionnées`}
                    </span>
                    <ChevronDown size={13} style={{ flexShrink: 0, transform: showRoundFilterDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
                  </button>

                  {/* Popover / Dropdown Menu for Rounds */}
                  {showRoundFilterDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        width: '240px',
                        background: '#0f172a',
                        border: '1px solid #334155',
                        borderRadius: '8px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.6)',
                        zIndex: 100,
                        padding: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '3px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px', borderBottom: '1px solid #1e293b', marginBottom: '2px' }}>
                        <span style={{ fontSize: '0.68rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase' }}>Filtrer par tournée</span>
                        {unassignedSelectedRoundIds.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setUnassignedSelectedRoundIds([])}
                            style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: '0.66rem', cursor: 'pointer', padding: 0 }}
                          >
                            Réinitialiser
                          </button>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setUnassignedSelectedRoundIds([]);
                          setShowRoundFilterDropdown(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '5px 8px',
                          borderRadius: '4px',
                          background: unassignedSelectedRoundIds.length === 0 ? 'rgba(56, 189, 248, 0.2)' : 'transparent',
                          color: unassignedSelectedRoundIds.length === 0 ? '#38bdf8' : '#e2e8f0',
                          border: 'none',
                          fontSize: '0.72rem',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#38bdf8' }} />
                          <span>Toutes les tournées</span>
                        </div>
                        <span style={{ fontSize: '0.66rem', color: '#94a3b8' }}>({activeUnassignedStops.length})</span>
                      </button>

                      {rounds.map(r => {
                        const count = activeUnassignedStops.filter(us => {
                          const client = clients.find(c => c.id === us.client_id);
                          return (us.default_round_id || client?.default_round_id) === r.id;
                        }).length;
                        const isSelected = unassignedSelectedRoundIds.includes(r.id);

                        return (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setUnassignedSelectedRoundIds(unassignedSelectedRoundIds.filter(id => id !== r.id));
                              } else {
                                setUnassignedSelectedRoundIds([...unassignedSelectedRoundIds, r.id]);
                              }
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '5px 8px',
                              borderRadius: '4px',
                              background: isSelected ? 'rgba(56, 189, 248, 0.15)' : 'transparent',
                              color: isSelected ? '#38bdf8' : '#cbd5e1',
                              border: isSelected ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid transparent',
                              fontSize: '0.72rem',
                              cursor: 'pointer',
                              textAlign: 'left'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: r.color || '#22c55e' }} />
                              <span>{r.name}</span>
                            </div>
                            <span style={{ fontSize: '0.66rem', color: '#94a3b8' }}>({count})</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Sort by Column Toolbar (Minimalist) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: 0 }}>
                  <select
                    value={unassignedSortBy}
                    onChange={e => setUnassignedSortBy(e.target.value)}
                    style={{
                      width: '100%',
                      background: '#0f172a',
                      color: '#cbd5e1',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      padding: '4px 6px',
                      fontSize: '0.72rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="time_window">Tri: Créneau</option>
                    <option value="client">Tri: Client (A-Z)</option>
                    <option value="city">Tri: Ville</option>
                    <option value="weight">Tri: Poids</option>
                    <option value="volume">Tri: Volume</option>
                    <option value="service_time">Tri: Durée</option>
                    <option value="round">Tri: Tournée</option>
                  </select>

                  <button
                    type="button"
                    onClick={() => setUnassignedSortAsc(!unassignedSortAsc)}
                    title={unassignedSortAsc ? "Ordre croissant (cliquer pour inverser)" : "Ordre décroissant (cliquer pour inverser)"}
                    style={{
                      background: '#0f172a',
                      border: '1px solid #334155',
                      color: '#38bdf8',
                      borderRadius: '6px',
                      padding: '4px 6px',
                      fontSize: '0.7rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <ArrowUpDown size={12} />
                  </button>
                </div>
              </div>
            </div>

            {/* Unassigned Stops List */}
            <div
              style={{ flex: 1, overflowY: 'auto', padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}
              onDragOver={handleBucketDragOver}
              onDragEnter={handleBucketDragOver}
              onDrop={handleBucketDrop}
            >
              {displayedUnassignedStops.length === 0 ? (
                <div style={{ padding: '30px 15px', textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>
                  Aucune livraison à planifier correspondant aux filtres.
                </div>
              ) : (
                displayedUnassignedStops.map(us => {
                  const isItemDragged = dragItem?.type === 'UNASSIGNED_STOP' && dragItem.stop.id === us.id;
                  const isSelectedRow = selectedStopId === us.id;
                  const client = clients.find(c => c.id === us.client_id);
                  const defRoundId = us.default_round_id || client?.default_round_id;
                  const defRound = rounds.find(r => r.id === defRoundId);
                  const serviceTimeMins = us.service_time_mins || client?.default_service_time_mins || 10;

                  return (
                    <div
                      key={us.id}
                      id={`expedition-unassigned-stop-${us.id}`}
                      draggable={true}
                      onDragStart={e => handleUnassignedDragStart(e, us)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleMarkerClick(us)}
                      style={{
                        background: isSelectedRow ? 'rgba(56, 189, 248, 0.25)' : isItemDragged ? 'rgba(245, 158, 11, 0.15)' : '#1e293b',
                        border: isSelectedRow ? '2px solid #38bdf8' : '1px solid #334155',
                        boxShadow: isSelectedRow ? '0 0 12px rgba(56, 189, 248, 0.4)' : 'none',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        cursor: 'grab',
                        opacity: isItemDragged ? 0.4 : 1,
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {/* COL 1: ROUND DIRECTLY ABOVE REAL TIME WINDOWS */}
                      <div style={{ width: '90px', padding: '0 4px', textAlign: 'center', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                        {defRound && (
                          <span
                            style={{
                              background: 'rgba(15, 23, 42, 0.9)',
                              border: `1px solid ${defRound.color || '#22c55e'}`,
                              color: defRound.color || '#22c55e',
                              fontSize: '0.64rem',
                              fontWeight: '700',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              maxWidth: '85px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'inline-block'
                            }}
                            title={defRound.name}
                          >
                            {defRound.name}
                          </span>
                        )}
                        {renderStopRealTimeWindows(us, client, null, clientDefaultTimeWindows)}
                      </div>

                      {/* COL 2: STOP CODE (TOP), CLIENT (MIDDLE, CLICKABLE), ADDRESS (BOTTOM) */}
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px', padding: '0 4px' }}>
                        <span
                          className="hover-underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedStopModal(us);
                          }}
                          style={{
                            fontSize: '0.68rem',
                            color: '#94a3b8',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: 'pointer'
                          }}
                          title={us.stop_number || `STOP-${us.id}`}
                        >
                          {us.stop_number || `STOP-${us.id}`}
                        </span>
                        <strong
                          className="hover-underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!us.client_id) return;
                            const cl = clients.find(c => Number(c.id) === Number(us.client_id) || String(c.id) === String(us.client_id)) || { id: us.client_id, name: us.client_name, address: us.address, city: us.city };
                            setSelectedClientModal(cl);
                          }}
                          style={{
                            color: '#ffffff',
                            fontSize: '0.76rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'inline-block',
                            cursor: 'pointer'
                          }}
                          title={us.client_name}
                        >
                          {us.client_name}
                        </strong>
                        <div
                          style={{
                            fontSize: '0.68rem',
                            color: '#94a3b8',
                            marginTop: '1px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={`${us.address}, ${us.city}`}
                        >
                          {us.address}, {us.city}
                        </div>
                      </div>

                      {/* COL 3: SERVICE TIME ABOVE WEIGHT & VOLUME (DELIVERY GREY, PICKUP GREEN) */}
                      <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end', minWidth: '65px' }}>
                        <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                          {serviceTimeMins} min
                        </span>
                        <span style={{ fontWeight: us.type === 'pickup' ? '600' : '400', color: us.type === 'pickup' ? '#22c55e' : '#94a3b8', fontSize: '0.72rem' }}>
                          {us.type === 'pickup' ? '+' : '-'}{Math.round(us.weight_kg || 0)} kg
                        </span>
                        <span style={{ fontWeight: us.type === 'pickup' ? '600' : '400', color: us.type === 'pickup' ? '#22c55e' : '#94a3b8', fontSize: '0.72rem' }}>
                          {us.type === 'pickup' ? '+' : '-'}{us.volume_m3 || 0.5} m³
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* SUIVI EN TEMPS RÉEL DRAWER */}
        {showChronologicalDrawer && (
          <div className="left-detail-pane" style={{ width: '440px', flexShrink: 0, background: '#182030', borderLeft: '1px solid #334155', display: 'flex', flexDirection: 'column' }}>
            {/* Drawer Header */}
            <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Activity size={18} color="#22c55e" /> Suivi en temps réel
              </h3>
              <button onClick={() => setShowChronologicalDrawer(false)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            {/* Filter Option: Tous les arrêts */}
            <div style={{ background: '#0f172a', padding: '8px 14px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.8rem', color: '#cbd5e1', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={showAllRealtimeStops}
                  onChange={(e) => setShowAllRealtimeStops(e.target.checked)}
                  style={{ accentColor: '#38bdf8', cursor: 'pointer', width: '15px', height: '15px' }}
                />
                <span>Tous les arrêts</span>
              </label>
              <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                {sortedChronologicalStops.length} arrêt{sortedChronologicalStops.length > 1 ? 's' : ''}
              </span>
            </div>

            {/* Stops Table */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0', display: 'flex', flexDirection: 'column' }}>
              <table className="data-table" style={{ background: 'transparent', border: 'none', width: '100%' }}>
                <tbody>
                  {sortedChronologicalStops.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', padding: '30px 14px', color: '#94a3b8', fontSize: '0.82rem' }}>
                        Aucun arrêt validé pour le moment aujourd'hui.
                        <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '4px' }}>
                          Cochez « Tous les arrêts » pour voir la totalité des missions prévues.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    sortedChronologicalStops.map(s => {
                      const isBucket = s.job_id === null || s.job_id === undefined;
                      const isDelivered = s.status === 'delivered';
                      const isFailed = s.status === 'failed';
                      const isValidated = isDelivered || isFailed || !!s.real_departure || (!!s.actual_arrival && isStopValidated(s));

                      const client = clients.find(c => c.id === s.client_id);

                      // Departure / Validation time
                      let depTimeFormatted = '--';
                      let depTimeColor = '#94a3b8';
                      let depTimeWeight = '500';

                      if (isBucket) {
                        depTimeFormatted = '--';
                        depTimeColor = '#94a3b8';
                        depTimeWeight = '500';
                      } else if (isValidated) {
                        depTimeFormatted = formatLocalTimeHHMM(s.real_departure || s.actual_arrival);
                        depTimeColor = '#22c55e';
                        depTimeWeight = '700';
                      } else if (s.estimated_departure) {
                        depTimeFormatted = s.estimated_departure.slice(0, 5);
                        depTimeColor = '#94a3b8';
                        depTimeWeight = '500';
                      } else {
                        depTimeFormatted = calcDepartureTimeStr(s.estimated_arrival?.slice(0, 5) || '08:00', s.service_time_mins || 10, s, clientDefaultTimeWindows);
                        depTimeColor = '#94a3b8';
                        depTimeWeight = '500';
                      }

                      let statusDotBg = '#64748b';
                      if (isDelivered) statusDotBg = '#22c55e';
                      else if (isFailed) statusDotBg = '#ef4444';
                      else if (!isBucket) statusDotBg = '#38bdf8';

                      const isSelected = selectedStopId === s.id;

                      return (
                        <tr
                          key={s.id}
                          onClick={() => handleMarkerClick(s)}
                          style={{
                            background: isSelected ? 'rgba(56, 189, 248, 0.25)' : '#1e293b',
                            borderBottom: '1px solid #334155',
                            borderLeft: isSelected ? '5px solid #38bdf8' : 'none',
                            boxShadow: isSelected ? 'inset 0 0 10px rgba(56, 189, 248, 0.25)' : 'none',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {/* COL 1: DEPARTURE / VALIDATION TIME */}
                          <td style={{ padding: '6px 6px', textAlign: 'center', width: '56px', verticalAlign: 'middle' }}>
                            <span
                              style={{
                                fontSize: '0.76rem',
                                fontWeight: depTimeWeight,
                                color: depTimeColor
                              }}
                              title={isBucket ? "Livraison à planifier (non assignée)" : isValidated ? "Heure de validation réelle" : "Heure de départ estimée"}
                            >
                              {depTimeFormatted}
                            </span>
                          </td>

                          {/* COL 2: TIME WINDOW CLOCK ICON */}
                          <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'middle', width: '36px' }}>
                            {renderRealtimeClockIcon(s, client, isBucket ? null : depTimeFormatted, clientDefaultTimeWindows)}
                          </td>

                          {/* COL 3: CLIENT & ADDRESS WITH STATUS DOT (VERTICALLY CENTERED LEFT CIRCLE & ALIGNED STOP CODE, NAME, ADDRESS) */}
                          <td style={{ padding: '6px 6px', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span
                                style={{
                                  background: statusDotBg,
                                  color: isFailed ? '#ffffff' : '#0f172a',
                                  width: '18px',
                                  height: '18px',
                                  borderRadius: '50%',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: '800',
                                  fontSize: '0.7rem',
                                  flexShrink: 0
                                }}
                              >
                                {isDelivered ? (
                                  <Check size={11} strokeWidth={3} />
                                ) : isFailed ? (
                                  <X size={11} strokeWidth={3} />
                                ) : isBucket ? (
                                  '•'
                                ) : (
                                  s.stop_order || '•'
                                )}
                              </span>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0, flex: 1 }}>
                                {/* 1. Stop Code */}
                                <span
                                  className="hover-underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedStopModal(s);
                                  }}
                                  style={{
                                    fontSize: '0.68rem',
                                    color: '#94a3b8',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer'
                                  }}
                                  title={s.stop_number || `STOP-${s.id}`}
                                >
                                  {s.stop_number || `STOP-${s.id}`}
                                </span>

                                {/* 2. Client Name */}
                                <strong
                                  className="hover-underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!s.client_id) return;
                                    const cl = clients.find(c => Number(c.id) === Number(s.client_id) || String(c.id) === String(s.client_id)) || { id: s.client_id, name: s.client_name, address: s.address, city: s.city };
                                    setSelectedClientModal(cl);
                                  }}
                                  style={{
                                    color: '#ffffff',
                                    fontSize: '0.76rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '180px',
                                    display: 'inline-block',
                                    cursor: 'pointer'
                                  }}
                                  title={s.client_name}
                                >
                                  {s.client_name}
                                </strong>

                                {/* 3. Address */}
                                <div
                                  style={{
                                    fontSize: '0.68rem',
                                    color: '#94a3b8',
                                    marginTop: '1px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '200px'
                                  }}
                                  title={`${s.address || ''}, ${(s.postal_code || client?.postal_code) ? (s.postal_code || client?.postal_code) + ' ' : ''}${s.city || ''}`}
                                >
                                  {s.address}, {(s.postal_code || client?.postal_code) ? `${s.postal_code || client?.postal_code} ` : ''}{s.city}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* COL 4: WEIGHT & VOLUME */}
                          <td style={{ padding: '6px 6px', textAlign: 'right', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end', minWidth: '60px' }}>
                              <span style={{ fontSize: '0.72rem', fontWeight: s.type === 'pickup' ? '600' : '400', color: s.type === 'pickup' ? '#22c55e' : '#94a3b8' }}>
                                {s.type === 'pickup' ? '+' : '-'}{Math.round(s.weight_kg || 0)} kg
                              </span>
                              <span style={{ fontSize: '0.72rem', fontWeight: s.type === 'pickup' ? '600' : '400', color: s.type === 'pickup' ? '#22c55e' : '#94a3b8' }}>
                                {s.type === 'pickup' ? '+' : '-'}{s.volume_m3 || 0.5} m³
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* UP TO 3 MULTI-OPEN JOBS SIDE-BY-SIDE */}
        {validOpenJobIds.map(jobId => {
          const job = jobs.find(j => j.id === jobId);
          if (!job) return null;

          const isJobStarted = job.status === 'in_progress' || job.status === 'completed' || job.status === 'done';

          // Use Estimated or Real depending on status
          const displayKm = parseFloat(isJobStarted ? (job.real_length_km || job.estimated_length_km || 0) : (job.estimated_length_km || 0));
          const displaySec = parseInt(isJobStarted ? (job.real_duration_sec || job.estimated_duration_sec || 0) : (job.estimated_duration_sec || 0));
          const displayCost = parseFloat(isJobStarted ? (job.real_cost || job.estimated_cost || 0) : (job.estimated_cost || 0));
          const departureTimeStr = job.estimated_departure || '07:30';

          const round = rounds.find(r => r.id === job.round_id);
          const roundNamePlaceholder = round ? round.name : 'Nom de la tournée...';

          const jobStopsList = stops
            .filter(s => s.job_id === jobId)
            .sort((a, b) => {
              if (isJobStarted) {
                const idxA = a.real_index !== null && a.real_index !== undefined ? parseInt(a.real_index) : parseInt(a.estimated_index || a.sequence_order || 0);
                const idxB = b.real_index !== null && b.real_index !== undefined ? parseInt(b.real_index) : parseInt(b.estimated_index || b.sequence_order || 0);
                return idxA - idxB;
              } else {
                const idxA = a.estimated_index !== null && a.estimated_index !== undefined ? parseInt(a.estimated_index) : parseInt(a.sequence_order || 0);
                const idxB = b.estimated_index !== null && b.estimated_index !== undefined ? parseInt(b.estimated_index) : parseInt(b.sequence_order || 0);
                return idxA - idxB;
              }
            });

          const truckInfo = trucks.find(t => t.id === job.truck_id);
          const payloadStats = calculateTruckPeakPayload(truckInfo, jobStopsList);

          const isPaneHovered = dragOverTarget?.type === 'JOB_PANE' && dragOverTarget.jobId === job.id;

          const calculatedReturnTime = calculateReturnTime(departureTimeStr, displaySec);

          const startHub = hubs.find(h => h.id === job?.start_hub_id) || hubs[0] || { name: 'Dépôt Principal', latitude: 46.5362, longitude: 6.5891 };
          const endHub = hubs.find(h => h.id === job?.end_hub_id) || startHub;

          return (
            <div
              key={job.id}
              id={`expedition-job-pane-${job.id}`}
              className="left-detail-pane"
              onDragOver={e => handleJobPaneDragOver(e, job.id)}
              onDragEnter={e => handleJobPaneDragOver(e, job.id)}
              onDrop={e => handleJobPaneDrop(e, job.id)}
              style={{
                width: '440px',
                minWidth: '440px',
                maxWidth: '560px',
                flexShrink: 0,
                background: isPaneHovered ? 'rgba(56, 189, 248, 0.12)' : '#182030',
                border: isPaneHovered ? '2px dashed #38bdf8' : '1px solid #334155',
                transition: 'all 0.15s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {(() => {
                const truckColor = truckInfo?.color || '#38bdf8';
                let headerBg = '#1e293b';
                if (job.status === 'in_progress') {
                  headerBg = 'rgba(56, 189, 248, 0.2)';
                } else if (job.status === 'completed' || job.status === 'done') {
                  headerBg = 'rgba(34, 197, 94, 0.2)';
                }

                const estimatedKm = parseFloat(job.estimated_length_km || 0);
                const estimatedSec = parseInt(job.estimated_duration_sec || 0);
                const estimatedCost = parseFloat(job.estimated_cost || 0);

                const isCompleted = job.status === 'completed' || job.status === 'done';
                const realKm = parseFloat(job.real_length_km || 0);
                const realSec = parseInt(job.real_duration_sec || 0);
                const realCost = parseFloat(job.real_cost || 0);

                return (
                  <>
                    <div style={{ background: headerBg, borderBottom: '1px solid #334155', padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderLeft: `5px solid ${truckColor}` }}>
                      <div style={{ flex: 1, minWidth: 0, marginRight: '8px' }}>
                        <JobTitleInput
                          jobId={job.id}
                          initialValue={job.display_name}
                          placeholder={roundNamePlaceholder}
                          onSave={onUpdateJob}
                        />
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {!isJobStarted && (
                          <>
                            {job.is_optimized ? (
                              <button
                                className="btn-action"
                                style={{ padding: '3px 8px', fontSize: '0.72rem', color: '#22c55e', borderColor: '#22c55e', background: 'rgba(34, 197, 94, 0.15)', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '700' }}
                                title="Tournée optimale - Cliquez pour ré-optimiser"
                                onClick={() => setSelectedJobForOptions(job)}
                              >
                                <Check size={13} />
                                <span>Optimal</span>
                              </button>
                            ) : (
                              <button
                                className="btn-action"
                                style={{ padding: '4px 6px', fontSize: '0.72rem', color: '#f59e0b', borderColor: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                title="Configurer et optimiser la tournée"
                                onClick={() => setSelectedJobForOptions(job)}
                                disabled={isLoadingPreview && optimizingJobId === job.id}
                              >
                                <Zap size={14} className={isLoadingPreview && optimizingJobId === job.id ? 'spin' : ''} color="#f59e0b" />
                              </button>
                            )}

                            <button
                              title="Supprimer la mission"
                              onClick={() => setConfirmDeleteJobId(job.id)}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', marginLeft: '4px' }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}

                        <button
                          title="Fermer"
                          onClick={() => handleCloseOpenJob(job.id)}
                          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px' }}
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>

                    {/* UNIFIED 3-ROW HEADER CONTROLS & METRICS BAR */}
                    <div style={{ background: 'rgba(56, 189, 248, 0.08)', padding: '8px 10px', borderBottom: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.72rem' }}>
                      
                      {/* ROW 1: ROUND DROPDOWN (LEFT) & DISTANCE (RIGHT) */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Route size={15} color="#22c55e" />
                          {isJobStarted ? (
                            <span style={{ color: '#ffffff', fontWeight: '400', fontSize: '0.72rem' }}>
                              {round ? `${round.name} (${round.code})` : '--'}
                            </span>
                          ) : (
                            <select
                              className="table-input"
                              style={{
                                width: 'auto',
                                padding: '2px 5px',
                                fontSize: '0.7rem',
                                background: '#0f172a',
                                color: '#ffffff',
                                border: !job.round_id ? '1px solid #f59e0b' : '1px solid #334155',
                                boxShadow: !job.round_id ? '0 0 4px rgba(245, 158, 11, 0.25)' : 'none',
                                fontWeight: '400'
                              }}
                              value={job.round_id || ''}
                              onChange={e => onUpdateJob(job.id, { round_id: e.target.value ? parseInt(e.target.value) : null })}
                            >
                              <option value="">--</option>
                              {rounds.map(r => (
                                <option key={r.id} value={r.id}>{r.name} ({r.code})</option>
                              ))}
                            </select>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Distance estimée (et réelle si clôturée)">
                          <Navigation size={13} color="#22c55e" />
                          <span style={{ color: '#cbd5e1', fontWeight: '400' }}>{job.truck_id ? `${estimatedKm.toFixed(1)} km` : '--'}</span>
                          {isCompleted && job.truck_id && (
                            <span style={{ color: realKm <= estimatedKm ? '#22c55e' : '#ef4444', fontWeight: '700', fontSize: '0.68rem', marginLeft: '2px' }}>
                              ({realKm.toFixed(1)} km)
                            </span>
                          )}
                        </div>
                      </div>

                      {/* ROW 2: DRIVER DROPDOWN (LEFT) & DURATION / TIME (RIGHT) */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <User size={15} color="#38bdf8" />
                          {isJobStarted ? (
                            <span style={{ color: '#ffffff', fontWeight: '400', fontSize: '0.72rem' }}>
                              {drivers.find(d => d.id === job.driver_id) ? `${drivers.find(d => d.id === job.driver_id).first_name} ${drivers.find(d => d.id === job.driver_id).last_name}` : '--'}
                            </span>
                          ) : (
                            <select
                              className="table-input"
                              style={{
                                width: 'auto',
                                padding: '2px 5px',
                                fontSize: '0.7rem',
                                background: '#0f172a',
                                color: '#ffffff',
                                border: !job.driver_id ? '1px solid #f59e0b' : '1px solid #334155',
                                boxShadow: !job.driver_id ? '0 0 4px rgba(245, 158, 11, 0.25)' : 'none',
                                fontWeight: '400'
                              }}
                              value={job.driver_id || ''}
                              onChange={e => onUpdateJob(job.id, { driver_id: e.target.value ? parseInt(e.target.value) : null })}
                            >
                              <option value="">--</option>
                              {drivers.map(d => (
                                <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                              ))}
                            </select>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Durée estimée (et réelle si clôturée)">
                          <Clock size={13} color="#38bdf8" />
                          <span style={{ color: '#cbd5e1', fontWeight: '400' }}>{job.truck_id ? formatDurationSec(estimatedSec) : '--'}</span>
                          {isCompleted && job.truck_id && (
                            <span style={{ color: realSec <= estimatedSec ? '#22c55e' : '#ef4444', fontWeight: '700', fontSize: '0.68rem', marginLeft: '2px' }}>
                              ({formatDurationSec(realSec)})
                            </span>
                          )}
                        </div>
                      </div>

                      {/* ROW 3: TRUCK DROPDOWN + CAPACITIES (LEFT), COST (RIGHT) */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Truck size={15} color="#ef4444" />
                          {isJobStarted ? (
                            <span style={{ color: '#ffffff', fontWeight: '400', fontSize: '0.72rem' }}>
                              {trucks.find(t => t.id === job.truck_id)?.plate || '--'}
                            </span>
                          ) : (
                            <select
                              className="table-input"
                              style={{
                                width: 'auto',
                                padding: '2px 5px',
                                fontSize: '0.7rem',
                                background: '#0f172a',
                                color: '#ffffff',
                                border: !job.truck_id ? '1px solid #f59e0b' : '1px solid #334155',
                                boxShadow: !job.truck_id ? '0 0 4px rgba(245, 158, 11, 0.25)' : 'none',
                                fontWeight: '400'
                              }}
                              value={job.truck_id || ''}
                              onChange={e => onUpdateJob(job.id, { truck_id: e.target.value ? parseInt(e.target.value) : null })}
                            >
                              <option value="">--</option>
                              {trucks.map(t => (
                                <option key={t.id} value={t.id}>{t.plate}</option>
                              ))}
                            </select>
                          )}

                          {/* Truck Peak Payload Stats directly to the right of truck */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '0.68rem',
                              fontWeight: '400',
                              background: payloadStats.isOverweight || payloadStats.isOvervolume ? 'rgba(239, 68, 68, 0.25)' : 'transparent',
                              border: payloadStats.isOverweight || payloadStats.isOvervolume ? '1px solid #ef4444' : 'none',
                              padding: '2px 6px',
                              borderRadius: '4px'
                            }}
                            title={`Charge crête durant la tournée: ${payloadStats.peakWeight} / ${payloadStats.maxWeight} kg | ${payloadStats.peakVolume} / ${payloadStats.maxVolume} m³`}
                          >
                            <span style={{ color: payloadStats.isOverweight ? '#ef4444' : '#ffffff' }}>
                              {formatWeightPair(payloadStats.peakWeight, payloadStats.maxWeight)}
                            </span>
                            <span style={{ color: '#64748b' }}>|</span>
                            <span style={{ color: payloadStats.isOvervolume ? '#ef4444' : '#ffffff' }}>
                              {payloadStats.peakVolume}/{payloadStats.maxVolume}m³
                            </span>
                          </div>
                        </div>

                        {/* Cost Metric Right-Aligned */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Coût estimé (et réel si clôturé)">
                          <DollarSign size={13} color="#ef4444" />
                          <span style={{ color: '#cbd5e1', fontWeight: '400' }}>{job.truck_id ? `${estimatedCost.toFixed(2)} CHF` : '--'}</span>
                          {isCompleted && job.truck_id && (
                            <span style={{ color: realCost <= estimatedCost ? '#22c55e' : '#ef4444', fontWeight: '700', fontSize: '0.68rem', marginLeft: '2px' }}>
                              ({realCost.toFixed(2)} CHF)
                            </span>
                          )}
                        </div>
                      </div>

                    </div>
                  </>
                );
              })()}

              {/* CONTINUOUS SCROLLABLE CONTENT BODY (DÉPART -> STOPS -> TRAJET END -> RETOUR) */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '0', display: 'flex', flexDirection: 'column' }}>
                
                {/* DÉPART HEADER BLOCK (JUST ABOVE FIRST STOP) */}
                <div style={{ background: '#0f172a', padding: '6px 10px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.7rem', color: '#94a3b8' }}>
                  <Building size={13} color="#94a3b8" />
                  <span>Départ :</span>
                  {isJobStarted ? (
                    <span style={{ fontSize: '0.72rem', color: '#22c55e', fontWeight: '700' }}>
                      {job.real_start ? formatLocalTimeHHMM(job.real_start) : departureTimeStr.slice(0, 5)}
                    </span>
                  ) : (
                    <input
                      type="time"
                      className="table-input"
                      style={{ width: '84px', padding: '2px 6px', fontSize: '0.72rem', background: '#1e293b', color: '#ffffff', border: '1px solid #334155', borderRadius: '4px', fontWeight: '400' }}
                      value={departureTimeStr.slice(0,5)}
                      onChange={e => onUpdateJob(job.id, { estimated_departure: e.target.value })}
                    />
                  )}
                  <select
                    className="table-input"
                    disabled={isJobStarted}
                    style={{ padding: '2px 6px', fontSize: '0.72rem', background: '#1e293b', color: isJobStarted ? '#94a3b8' : '#ffffff', border: '1px solid #334155', fontWeight: '400', opacity: isJobStarted ? 0.7 : 1 }}
                    value={job.start_hub_id || 1}
                    onChange={e => onUpdateJob(job.id, { start_hub_id: parseInt(e.target.value) })}
                  >
                    {hubs.map(h => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                </div>

                {/* TRAJET 1: START HUB TO FIRST STOP & PAUSES */}
                {jobStopsList.length > 0 && (() => {
                  const firstStop = jobStopsList[0];
                  const isJobClosed = job.status === 'completed' || job.status === 'done';
                  const isFirstStopDone = isStopValidated(firstStop);
                  const pausesBeforeFirst = (jobPauses || []).filter(p => {
                    if (parseInt(p.job_id) !== parseInt(job.id)) return false;
                    if (parseInt(p.before_stop_id) !== parseInt(firstStop.id)) return false;
                    const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                    if (isReal) return true;
                    return !isJobClosed && !isFirstStopDone;
                  });
                  const firstPauseMins = pausesBeforeFirst.reduce((acc, p) => acc + parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10), 0);
                  return (
                    <>
                      <div style={{ background: '#0f172a', padding: '5px 12px', borderBottom: '1px dashed #334155', fontSize: '0.7rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px', textAlign: 'left' }}>
                        <Truck size={13} color="#94a3b8" />
                        <span>Trajet : <b style={{ color: '#94a3b8', fontWeight: '400' }}>{calcHubToStopDriveMins(startHub, firstStop, departureTimeStr, firstPauseMins)} min&nbsp;&nbsp;&nbsp;{firstStop.leg_distance_km !== undefined ? firstStop.leg_distance_km : calcHaversineDistanceKm(startHub.latitude, startHub.longitude, firstStop.latitude, firstStop.longitude)} km</b></span>
                      </div>
                      {pausesBeforeFirst.map(p => {
                        const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                        const pauseDuration = parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10);
                        const timeWindowStr = p.start && p.end ? `(${p.start} - ${p.end})` : '';
                        return (
                          <div key={`pause-first-${p.id}`} style={{ background: '#0f172a', padding: '4px 12px', borderBottom: '1px dashed #334155', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.7rem', color: '#94a3b8' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                              <Pause size={13} color="#94a3b8" />
                              <span>
                                {isReal ? (
                                  <>
                                    <span>Pause réelle : </span>
                                    <b style={{ color: '#22c55e', fontWeight: '700' }}>{pauseDuration} min</b>
                                    {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                  </>
                                ) : (
                                  <>
                                    <span>Pause : </span>
                                    <b style={{ color: '#94a3b8', fontWeight: '500' }}>{pauseDuration} min</b>
                                    {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                  </>
                                )}
                              </span>
                            </div>
                            {!isJobStarted && !isReal && (
                              <button
                                type="button"
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', display: 'inline-flex', alignItems: 'center' }}
                                title="Supprimer la pause"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onDeletePause) onDeletePause(p.id);
                                }}
                              >
                                <X size={14} color="#ef4444" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </>
                  );
                })()}

                {/* STOPS TABLE */}
                <table className="data-table" style={{ background: 'transparent', border: 'none', width: '100%' }}>
                  <tbody>
                    {jobStopsList.map((stop, idx) => {
                      const isRowHovered = dragOverTarget?.type === 'STOP_ROW' && dragOverTarget.jobId === job.id && dragOverTarget.index === idx;
                      const isItemDragged = dragItem?.type === 'STOP' && dragItem.stop.id === stop.id;
                      const isSelectedRow = selectedStopId === stop.id;
                      const client = clients.find(c => c.id === stop.client_id);
                      
                      const arrivalTimeFormatted = stop.estimated_arrival ? stop.estimated_arrival.slice(0,5) : '08:00';
                      const serviceMins = parseInt(stop.service_time_mins || 10);
                      const departureTimeFormatted = calcDepartureTimeStr(arrivalTimeFormatted, serviceMins, stop, clientDefaultTimeWindows);

                      const stopDisplayNum = isJobStarted
                        ? (stop.real_index !== null && stop.real_index !== undefined ? stop.real_index : (stop.estimated_index || idx + 1))
                        : (stop.estimated_index !== null && stop.estimated_index !== undefined ? stop.estimated_index : (idx + 1));

                      // Stop timings 4-state logic (Real = Green, Estimated/Unexecuted = Grey)
                      const hasRealArrival = !!stop.actual_arrival;
                      const isDoneStop = isStopValidated(stop);

                      let arrivalDisplay = arrivalTimeFormatted;
                      let arrivalColor = '#94a3b8';
                      let arrivalWeight = '500';
                      let arrivalTitle = "Heure d'arrivée estimée";

                      let serviceDisplay = `${serviceMins}m`;
                      let serviceColor = '#94a3b8';
                      let serviceWeight = '500';
                      let serviceTitle = "Temps de service estimé";

                      let departureDisplay = departureTimeFormatted;
                      let departureColor = '#64748b';
                      let departureWeight = '500';
                      let departureTitle = "Heure de départ estimée";

                      if (hasRealArrival && !isDoneStop) {
                        // State 2: marked as arrived and nothing else -> Green real arrival, Grey estimated service & departure based on arrival
                        arrivalDisplay = formatLocalTimeHHMM(stop.actual_arrival);
                        arrivalColor = '#22c55e';
                        arrivalWeight = '700';
                        arrivalTitle = "Heure d'arrivée réelle";

                        serviceColor = '#94a3b8';
                        serviceWeight = '500';
                        serviceTitle = "Temps de service estimé";

                        departureDisplay = calcDepartureTimeStr(arrivalDisplay, serviceMins, stop, clientDefaultTimeWindows);
                        departureColor = '#64748b';
                        departureWeight = '500';
                        departureTitle = "Heure de départ estimée";
                      } else if (!hasRealArrival && isDoneStop) {
                        // State 3: validated and never marked as arrived -> Grey estimated arrival & service, Green real departure
                        arrivalDisplay = arrivalTimeFormatted;
                        arrivalColor = '#94a3b8';
                        arrivalWeight = '500';
                        arrivalTitle = "Heure d'arrivée estimée";

                        serviceColor = '#94a3b8';
                        serviceWeight = '500';
                        serviceTitle = "Temps de service estimé";

                        departureDisplay = stop.real_departure ? formatLocalTimeHHMM(stop.real_departure) : departureTimeFormatted;
                        departureColor = '#22c55e';
                        departureWeight = '700';
                        departureTitle = "Heure de départ réelle";
                      } else if (hasRealArrival && isDoneStop) {
                        // State 4: both marked as arrived and validated -> Green real arrival, Green real computed service, Green real departure
                        arrivalDisplay = formatLocalTimeHHMM(stop.actual_arrival);
                        arrivalColor = '#22c55e';
                        arrivalWeight = '700';
                        arrivalTitle = "Heure d'arrivée réelle";

                        let realServiceMins = serviceMins;
                        if (stop.real_departure && stop.actual_arrival) {
                          const arrDate = parseToDate(stop.actual_arrival);
                          const depDate = parseToDate(stop.real_departure);
                          if (arrDate && depDate) {
                            const diffMs = depDate.getTime() - arrDate.getTime();
                            if (!isNaN(diffMs) && diffMs >= 0) {
                              realServiceMins = Math.max(1, Math.round(diffMs / 60000));
                            }
                          }
                        }
                        serviceDisplay = `${realServiceMins}m`;
                        serviceColor = '#22c55e';
                        serviceWeight = '700';
                        serviceTitle = "Temps de service réel";

                        departureDisplay = stop.real_departure ? formatLocalTimeHHMM(stop.real_departure) : departureTimeFormatted;
                        departureColor = '#22c55e';
                        departureWeight = '700';
                        departureTitle = "Heure de départ réelle";
                      } else {
                        // State 1: not arrived, still planned -> All values are estimated and grey
                        arrivalDisplay = arrivalTimeFormatted;
                        arrivalColor = '#94a3b8';
                        arrivalWeight = '500';
                        arrivalTitle = "Heure d'arrivée estimée";

                        serviceDisplay = `${serviceMins}m`;
                        serviceColor = '#94a3b8';
                        serviceWeight = '500';
                        serviceTitle = "Temps de service estimé";

                        departureDisplay = departureTimeFormatted;
                        departureColor = '#64748b';
                        departureWeight = '500';
                        departureTitle = "Heure de départ estimée";
                      }

                      return (
                        <React.Fragment key={stop.id}>
                          <tr
                            key={stop.id}
                            id={`expedition-stop-row-${stop.id}`}
                            draggable={!isDoneStop}
                            onDragStart={e => !isDoneStop && handleStopDragStart(e, stop, job.id, idx)}
                            onDragEnd={handleDragEnd}
                            onDragOver={e => handleStopRowDragOver(e, job.id, idx)}
                            onDrop={e => handleStopRowDrop(e, job.id, idx)}
                            onClick={() => handleMarkerClick(stop)}
                            style={{
                              background: isSelectedRow ? 'rgba(56, 189, 248, 0.28)' : isRowHovered ? 'rgba(56, 189, 248, 0.18)' : isItemDragged ? 'rgba(239, 68, 68, 0.2)' : '#1e293b',
                              opacity: isItemDragged ? 0.4 : 1,
                              borderTop: isRowHovered ? '2px solid #38bdf8' : undefined,
                              borderLeft: isSelectedRow ? '5px solid #38bdf8' : 'none',
                              boxShadow: isSelectedRow ? 'inset 0 0 10px rgba(56, 189, 248, 0.25)' : 'none',
                              cursor: isDoneStop ? 'default' : 'grab',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            {/* COL 0: STOP NUMBER BADGE (VERY LEFT) */}
                            <td style={{ padding: '6px 4px 6px 8px', textAlign: 'center', width: '28px', verticalAlign: 'middle' }}>
                              <span
                                style={{
                                  background: stop.status === 'delivered' ? '#22c55e' : stop.status === 'failed' ? '#ef4444' : '#38bdf8',
                                  color: stop.status === 'failed' ? '#ffffff' : '#0f172a',
                                  width: '18px',
                                  height: '18px',
                                  borderRadius: '50%',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: '800',
                                  fontSize: '0.7rem',
                                  flexShrink: 0
                                }}
                              >
                                {stopDisplayNum}
                              </span>
                            </td>

                            {/* COL 1: TIMING STACK (4 STATES DYNAMIC TIMINGS) */}
                            <td style={{ padding: '6px 6px', textAlign: 'center', width: '56px', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.72rem', fontWeight: arrivalWeight, color: arrivalColor }} title={arrivalTitle}>
                                  {arrivalDisplay}
                                </span>
                                <span style={{ fontSize: '0.66rem', fontWeight: serviceWeight, color: serviceColor }} title={serviceTitle}>
                                  {serviceDisplay}
                                </span>
                                <span style={{ fontSize: '0.7rem', fontWeight: departureWeight, color: departureColor }} title={departureTitle}>
                                  {departureDisplay}
                                </span>
                              </div>
                            </td>

                            {/* COL 2: REAL TIME WINDOWS COLUMN */}
                            <td style={{ padding: '6px 4px', textAlign: 'center', verticalAlign: 'middle', width: '90px' }}>
                              {renderStopRealTimeWindows(stop, client, arrivalTimeFormatted, clientDefaultTimeWindows)}
                            </td>

                            {/* COL 3: CLIENT & ADDRESS */}
                            <td style={{ padding: '6px 6px', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
                                {/* 1. Stop Code */}
                                <span
                                  className="hover-underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedStopModal(stop);
                                  }}
                                  style={{
                                    fontSize: '0.68rem',
                                    color: '#94a3b8',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer'
                                  }}
                                  title={stop.stop_number || `STOP-${stop.id}`}
                                >
                                  {stop.stop_number || `STOP-${stop.id}`}
                                </span>

                                {/* 2. Client Name */}
                                <strong
                                  className="hover-underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!stop.client_id) return;
                                    const cl = clients.find(c => Number(c.id) === Number(stop.client_id) || String(c.id) === String(stop.client_id)) || { id: stop.client_id, name: stop.client_name, address: stop.address, city: stop.city };
                                    setSelectedClientModal(cl);
                                  }}
                                  style={{
                                    color: '#ffffff',
                                    fontSize: '0.76rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '160px',
                                    display: 'inline-block',
                                    cursor: 'pointer'
                                  }}
                                  title={stop.client_name}
                                >
                                  {stop.client_name}
                                </strong>

                                {/* 3. Address (1234 Cityname format) */}
                                {(() => {
                                  const postal = stop.postal_code || client?.postal_code;
                                  const city = stop.city || client?.city;
                                  let zipCity = [postal, city].filter(Boolean).join(' ');
                                  if (!zipCity && stop.address) {
                                    const match = stop.address.match(/(?:,\s*)?(\b\d{4,5}\b)\s+([A-Za-zÀ-ÖØ-öø-ÿ\s\-]+)$/);
                                    if (match) {
                                      zipCity = `${match[1]} ${match[2].trim()}`;
                                    } else {
                                      zipCity = stop.address;
                                    }
                                  }
                                  const fullAddressTitle = [stop.address, [postal, city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                                  return (
                                    <div
                                      style={{
                                        fontSize: '0.68rem',
                                        color: '#94a3b8',
                                        marginTop: '1px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '180px'
                                      }}
                                      title={fullAddressTitle}
                                    >
                                      {zipCity || '--'}
                                    </div>
                                  );
                                })()}
                              </div>
                            </td>

                            {/* COL 4: WEIGHT & VOLUME (NO EDIT BUTTON) */}
                            <td style={{ padding: '6px 6px', textAlign: 'right', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end', minWidth: '60px' }}>
                                <span style={{ fontSize: '0.72rem', fontWeight: stop.type === 'pickup' ? '600' : '400', color: stop.type === 'pickup' ? '#22c55e' : '#94a3b8' }}>
                                  {stop.type === 'pickup' ? '+' : '-'}{Math.round(stop.weight_kg || 0)} kg
                                </span>
                                <span style={{ fontSize: '0.72rem', fontWeight: stop.type === 'pickup' ? '600' : '400', color: stop.type === 'pickup' ? '#22c55e' : '#94a3b8' }}>
                                  {stop.type === 'pickup' ? '+' : '-'}{stop.volume_m3 || 0.5} m³
                                </span>
                              </div>
                            </td>
                          </tr>

                          {/* INTER-STOP TRAJET & PAUSE ROWS */}
                          {idx < jobStopsList.length - 1 && (() => {
                            const nextStop = jobStopsList[idx + 1];
                            const isJobClosed = job.status === 'completed' || job.status === 'done';
                            const isNextStopDone = isStopValidated(nextStop);
                            const pausesForNextStop = (jobPauses || []).filter(p => {
                              if (parseInt(p.job_id) !== parseInt(job.id)) return false;
                              if (parseInt(p.before_stop_id) !== parseInt(nextStop.id)) return false;
                              const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                              if (isReal) return true;
                              return !isJobClosed && !isNextStopDone;
                            });

                            const prevDepTime = stop.real_departure || stop.actual_arrival;
                            const nextArrTime = nextStop.actual_arrival;

                            let isRealTrajet = false;
                            const totalNextPauseMins = pausesForNextStop.reduce((acc, p) => acc + parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10), 0);
                            let trajetMins = calcLegDriveTimeMins(stop, nextStop, totalNextPauseMins, clientDefaultTimeWindows);
                            const legDistanceKm = nextStop.leg_distance_km !== undefined
                              ? nextStop.leg_distance_km
                              : calcHaversineDistanceKm(stop.latitude, stop.longitude, nextStop.latitude, nextStop.longitude);

                            if (prevDepTime && nextArrTime) {
                              const tDep = new Date(prevDepTime).getTime();
                              const tArr = new Date(nextArrTime).getTime();
                              if (!isNaN(tDep) && !isNaN(tArr) && tArr >= tDep) {
                                isRealTrajet = true;
                                trajetMins = Math.max(1, Math.round((tArr - tDep) / 60000) - totalNextPauseMins);
                                if (trajetMins < 1) trajetMins = 1;
                              }
                            }

                            return (
                              <React.Fragment key={`leg-${stop.id}`}>
                                <tr style={{ background: '#0f172a' }}>
                                  <td colSpan={5} style={{ padding: '4px 10px', borderTop: '1px dashed #334155', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.7rem', color: '#94a3b8' }}>
                                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                        <Truck size={13} color="#94a3b8" />
                                        <span>Trajet : <b style={{ color: isRealTrajet ? '#22c55e' : '#94a3b8', fontWeight: isRealTrajet ? '700' : '400' }}>{trajetMins} min</b>&nbsp;&nbsp;&nbsp;<span style={{ color: '#94a3b8' }}>{legDistanceKm} km</span></span>
                                      </div>

                                      {!isStopValidated(nextStop) && (
                                        <button
                                          type="button"
                                          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px', display: 'inline-flex', alignItems: 'center' }}
                                          title="Ajouter une pause après ce trajet"
                                          onClick={() => {
                                            const val = window.prompt("Durée de la pause en minutes :", "15");
                                            if (val && !isNaN(parseInt(val))) {
                                              if (onAddPause) onAddPause(job.id, nextStop.id, parseInt(val));
                                            }
                                          }}
                                        >
                                          <Pause size={13} color="#94a3b8" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>

                                {pausesForNextStop.map(p => {
                                  const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                                  const pauseDuration = parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10);
                                  const timeWindowStr = p.start && p.end ? `(${p.start} - ${p.end})` : '';
                                  return (
                                    <tr key={`pause-${p.id}`} style={{ background: '#0f172a' }}>
                                      <td colSpan={5} style={{ padding: '4px 10px', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.7rem', color: '#94a3b8' }}>
                                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            <Pause size={13} color="#94a3b8" />
                                            <span>
                                              {isReal ? (
                                                <>
                                                  <span>Pause réelle : </span>
                                                  <b style={{ color: '#22c55e', fontWeight: '700' }}>{pauseDuration} min</b>
                                                  {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                                </>
                                              ) : (
                                                <>
                                                  <span>Pause : </span>
                                                  <b style={{ color: '#94a3b8', fontWeight: '500' }}>{pauseDuration} min</b>
                                                  {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                                </>
                                              )}
                                            </span>
                                          </div>

                                          {!isJobStarted && !isReal && (
                                            <button
                                              type="button"
                                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', display: 'inline-flex', alignItems: 'center' }}
                                              title="Supprimer la pause"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (onDeletePause) onDeletePause(p.id);
                                              }}
                                            >
                                              <X size={14} color="#ef4444" />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })()}
                        </React.Fragment>
                      );
                    })}

                    {/* TRAILING PAUSES */}
                    {(jobPauses || []).filter(p => {
                      if (parseInt(p.job_id) !== parseInt(job.id)) return false;
                      if (jobStopsList.some(s => parseInt(s.id) === parseInt(p.before_stop_id))) return false;
                      const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                      if (isReal) return true;
                      const isJobClosed = job.status === 'completed' || job.status === 'done';
                      const hasPendingStops = jobStopsList.some(s => !isStopValidated(s));
                      return !isJobClosed && hasPendingStops;
                    }).map(p => {
                      const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                      const pauseDuration = parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10);
                      const timeWindowStr = p.start && p.end ? `(${p.start} - ${p.end})` : '';
                      return (
                        <tr key={`pause-trailing-${p.id}`} style={{ background: '#0f172a' }}>
                          <td colSpan={4} style={{ padding: '4px 10px', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.7rem', color: '#94a3b8' }}>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <Pause size={13} color="#94a3b8" />
                                <span>
                                  {isReal ? (
                                    <>
                                      <span>Pause réelle : </span>
                                      <b style={{ color: '#22c55e', fontWeight: '700' }}>{pauseDuration} min</b>
                                      {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                    </>
                                  ) : (
                                    <>
                                      <span>Pause : </span>
                                      <b style={{ color: '#94a3b8', fontWeight: '500' }}>{pauseDuration} min</b>
                                      {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                    </>
                                  )}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* TRAJET END: LAST STOP TO END HUB */}
                {jobStopsList.length > 0 && (
                  <div style={{ background: '#0f172a', padding: '5px 12px', borderTop: '1px dashed #334155', fontSize: '0.7rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px', textAlign: 'left' }}>
                    <Truck size={13} color="#94a3b8" />
                    <span>Trajet : <b style={{ color: '#94a3b8', fontWeight: '400' }}>{calcStopToHubDriveMins(jobStopsList[jobStopsList.length - 1], endHub, calculatedReturnTime, job, clientDefaultTimeWindows)} min&nbsp;&nbsp;&nbsp;{job.return_leg_distance_km !== undefined ? job.return_leg_distance_km : calcHaversineDistanceKm(jobStopsList[jobStopsList.length - 1].latitude, jobStopsList[jobStopsList.length - 1].longitude, endHub.latitude, endHub.longitude)} km</b></span>
                  </div>
                )}

                {/* RETOUR FOOTER BLOCK (PLAIN TEXT RETURN TIME) */}
                <div style={{ background: '#0f172a', padding: '6px 10px', borderTop: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.7rem', color: '#94a3b8' }}>
                  <Building size={13} color="#94a3b8" />
                  <span>Retour :</span>
                  <span style={{ fontSize: '0.72rem', color: job.real_end ? '#22c55e' : '#cbd5e1', fontWeight: job.real_end ? '700' : '400' }}>
                    {job.real_end ? formatLocalTimeHHMM(job.real_end) : calculatedReturnTime}
                  </span>
                  <select
                    className="table-input"
                    disabled={job.status === 'completed' || job.status === 'done'}
                    style={{ padding: '2px 6px', fontSize: '0.72rem', background: '#1e293b', color: (job.status === 'completed' || job.status === 'done') ? '#94a3b8' : '#ffffff', border: '1px solid #334155', fontWeight: '400', opacity: (job.status === 'completed' || job.status === 'done') ? 0.7 : 1 }}
                    value={job.end_hub_id || 1}
                    onChange={e => onUpdateJob(job.id, { end_hub_id: parseInt(e.target.value) })}
                  >
                    {hubs.map(h => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                </div>

              </div>
            </div>
          );
        })}

        {/* DRIVER TIMELINE LATERAL PANEL (ATTACHED TO LEFT MENU) */}
        {showDriverTimelinePanel && (
          <DriverTimelinePanel
            isOpen={showDriverTimelinePanel}
            onClose={() => setShowDriverTimelinePanel(false)}
            drivers={drivers}
            driverShifts={driverShifts}
            jobs={filteredJobs}
            stops={stops}
            trucks={trucks}
            clients={clients}
            currentDate={selectedDateISO}
            onSelectStop={(stop) => setSelectedStopModal(stop)}
            onSelectJob={(job) => setSelectedJobModal(job)}
          />
        )}

      </div>

      {/* RIGHT MAP VIEW */}
      <div className="expedition-right-map" style={{ flex: 1, height: '100%', position: 'relative' }}>
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

        {/* Top Right Floating Map Controls */}
        <div style={{ position: 'absolute', top: '14px', right: '14px', zIndex: 1000, display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Swaps interior color between status mode and job's truck color mode */}
          <button
            className="icon-btn-header"
            onClick={() => setMarkerColorMode(prev => prev === 'status' ? 'truck' : 'status')}
            title={markerColorMode === 'status' ? "Afficher les couleurs par camion" : "Afficher les couleurs par statut"}
            style={{
              width: '36px',
              height: '36px',
              background: '#0f172a',
              border: markerColorMode === 'truck' ? '1px solid #38bdf8' : '1px solid #334155',
              boxShadow: markerColorMode === 'truck' ? '0 0 8px rgba(56, 189, 248, 0.4)' : 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justify: 'center'
            }}
          >
            {/* Half empty half full electric blue Circle Icon */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="#38bdf8" strokeWidth="2" />
              <path d="M12 3v18c4.97 0 9-4.03 9-9s-4.03-9-9-9z" fill="#38bdf8" />
            </svg>
          </button>

          {/* Floating Manual Recenter Button */}
          <button
            className="icon-btn-header"
            onClick={handleRecenterMap}
            title="Recadrer la vue d'ensemble"
            style={{ width: '36px', height: '36px' }}
          >
            <Focus size={18} />
          </button>
        </div>
      </div>

      {/* STOP CARD MODAL */}
      <StopCardModal
        stop={selectedStopModal}
        isOpen={!!selectedStopModal}
        onClose={() => setSelectedStopModal(null)}
        onSaveStop={async (id, data) => {
          await onUpdateStop(id, data);
          setSelectedStopModal(null);
        }}
        onDeleteStop={async (id) => {
          if (onDeleteStop) {
            await onDeleteStop(id);
          } else {
            await fetch(`/api/stops/${id}`, { method: 'DELETE' });
            if (onReloadData) await onReloadData();
          }
          setSelectedStopModal(null);
        }}
        jobs={jobs}
        clients={clients}
        rounds={rounds}
        stops={stops}
        unassignedStops={unassignedStops}
        stopDependencies={stopDependencies}
        onReloadData={onReloadData}
      />

      {/* CLIENT CARD MODAL */}
      <ClientCardModal
        client={selectedClientModal}
        isOpen={!!selectedClientModal}
        onClose={() => setSelectedClientModal(null)}
        onSaveClient={(id, data) => {
          if (onUpdateClient) {
            onUpdateClient(id, data);
          } else if (onReloadData) {
            onReloadData();
          }
        }}
        rounds={rounds}
        clientDefaultTimeWindows={clientDefaultTimeWindows}
        clientContacts={clientContacts}
        onAddContact={onAddContact}
        onDeleteContact={onDeleteContact}
        onAddClientTimeWindow={onAddClientTimeWindow}
        onUpdateClientTimeWindow={onUpdateClientTimeWindow}
        onDeleteClientTimeWindow={onDeleteClientTimeWindow}
        onReloadData={onReloadData}
      />

      {/* JOB CARD MODAL */}
      <JobCardModal
        job={selectedJobModal}
        isOpen={!!selectedJobModal}
        onClose={() => setSelectedJobModal(null)}
        onSaveJob={async (id, data) => {
          await onUpdateJob(id, data);
          setSelectedJobModal(null);
        }}
        drivers={drivers}
        trucks={trucks}
        hubs={hubs}
        stops={stops}
      />

      {/* PRE-OPTIMIZATION OPTIONS MODAL */}
      <JobOptimizationOptionsModal
        isOpen={!!selectedJobForOptions}
        onClose={() => setSelectedJobForOptions(null)}
        job={selectedJobForOptions}
        jobPauses={jobPauses}
        stops={stops}
        stopDependencies={stopDependencies}
        drivers={drivers}
        driverShifts={driverShifts}
        rounds={rounds}
        currentDate={selectedDateISO}
        onLaunchPreview={handleLaunchOptimizationPreview}
      />

      {/* JOB OPTIMIZATION COMPARISON MODAL */}
      <JobOptimizationModal
        isOpen={!!optimizationPreviewData}
        onClose={() => setOptimizationPreviewData(null)}
        previewData={optimizationPreviewData}
        onApplyOptimization={onApplyOptimization}
      />

      {/* MULTI-JOB CROSS OPTIMIZATION OPTIONS MODAL */}
      <CrossOptimizationOptionsModal
        isOpen={showCrossOptiOptionsModal}
        onClose={() => setShowCrossOptiOptionsModal(false)}
        unassignedStops={activeUnassignedStops}
        jobs={filteredJobs}
        trucks={trucks}
        drivers={drivers}
        driverShifts={driverShifts}
        rounds={rounds}
        stops={stops}
        stopDependencies={stopDependencies}
        currentDate={selectedDateISO}
        onLaunchCrossPreview={handleLaunchCrossPreview}
      />

      {/* MULTI-JOB CROSS OPTIMIZATION COMPARISON MODAL */}
      <CrossOptimizationModal
        isOpen={showCrossOptiModal}
        onClose={() => setShowCrossOptiModal(false)}
        previewData={crossOptiPreviewData}
        onApplyCrossOptimization={handleApplyCrossOptimization}
      />

      {/* CONFIRM DELETE JOB MODAL */}
      {confirmDeleteJobId && (
        <div className="modal-overlay">
          <div className="overlay-card" style={{ width: '380px' }}>
            <h3 style={{ color: '#ef4444', marginBottom: '10px' }}>Supprimer la mission #{confirmDeleteJobId} ?</h3>
            <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '16px' }}>
              Les livraisons associées ne seront pas supprimées et retourneront dans la liste <i>"Livraisons à planifier"</i>.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn-action" onClick={() => setConfirmDeleteJobId(null)}>Annuler</button>
              <button className="btn-action" style={{ background: '#ef4444', color: '#fff', border: 'none' }} onClick={() => handleConfirmDeleteJob(confirmDeleteJobId)}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM DELETE ALL PLANNED JOBS MODAL */}
      {showDeleteAllModal && (
        <div className="modal-overlay">
          <div className="overlay-card" style={{ width: '440px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Trash2 size={18} color="#ef4444" /> Supprimer les missions du jour
              </h3>
              <button onClick={() => setShowDeleteAllModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18}/></button>
            </div>

            <div style={{ background: 'rgba(239, 68, 68, 0.12)', border: '1px solid #ef4444', borderRadius: '6px', padding: '12px', marginBottom: '16px', color: '#fca5a5', fontSize: '0.78rem', lineHeight: '1.4' }}>
              <strong>⚠️ Attention :</strong> Seules les missions non démarrées de la journée sélectionnée (statut <em>"planned"</em>) seront supprimées ({filteredJobs.filter(j => j.status === 'planned').length} mission(s)).
              <br/><br/>
              Toutes leurs livraisons seront automatiquement remises dans le bucket <strong>"Livraisons à planifier"</strong>.
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-action" onClick={() => setShowDeleteAllModal(false)}>
                Annuler
              </button>
              <button
                type="button"
                className="btn-action"
                style={{ background: '#ef4444', color: '#ffffff', border: 'none', fontWeight: '700' }}
                onClick={async () => {
                  const targetJobIds = filteredJobs.filter(j => j.status === 'planned').map(j => j.id);
                  if (targetJobIds.length > 0) {
                    if (onDeleteAllPlannedJobs) {
                      await onDeleteAllPlannedJobs(targetJobIds);
                    } else {
                      const res = await fetch('/api/jobs/planned', {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ jobIds: targetJobIds })
                      });
                      if (res.ok && onReloadData) await onReloadData();
                    }
                    const plannedIds = new Set(targetJobIds);
                    setOpenJobIds(prev => prev.filter(id => !plannedIds.has(id)));
                  }
                  setShowDeleteAllModal(false);
                }}
              >
                Supprimer {filteredJobs.filter(j => j.status === 'planned').length} mission(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EXPEDITION DATE FILTER MODAL */}
      <CalendarFilterModal
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        type="expedition"
        title="Date d'expédition"
        subtitle="Sélectionnez un jour ou une plage de dates pour afficher les tournées et livraisons"
        currentFilter={dateFilter}
        onApplyFilter={handleApplyDateFilter}
      />
    </div>
  );
}
